import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip,
  CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select,
  Switch, FormControlLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip as MuiTooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, ReferenceLine, ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { useAuth } from '../context/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// "Shadows" tab (V1 / OPT-SPY-DAILY) — spec: remote prompt §"Shadows & Roadmap"
// (9/30/2026). Everything here is DATA ONLY: no shadow ever traded, nothing is
// in the P&L, no cell is a recommendation. Roster discipline: studies and
// roadmap items come from the prompt roster verbatim; graduated items are NOT
// shown even if their rows still arrive (they are calibration for live rules).
// ─────────────────────────────────────────────────────────────────────────────

interface StudyEntry {
  date: string;
  account?: string;
  window?: string;
  record?: { [k: string]: any };
  eod?: { [k: string]: any };
  fire?: { [k: string]: any };
  pref?: { [k: string]: any };
  // Study G v0.64.0 (G-live): the demoted −50% live giveback, back to shadow —
  // GIVEBACK_LIVE_SHADOW, once per trade, where it would have fired
  live_fire?: { [k: string]: any };
  // Study J (v0.52.0) fire-detail rows, joined on opened_at
  bleed_fire?: { [k: string]: any };
  magnet_fire?: { [k: string]: any };
  early_mixed_fire?: { [k: string]: any };
  // Study K (v0.53.0): leader OPEN/CLOSE pair + bars-based extreme row;
  // "cand|date" entries carry IV_MARGIN_SHADOW cycles that never filled
  open?: { [k: string]: any };
  close?: { [k: string]: any };
  session_extreme?: { [k: string]: any };
  candidates?: { [k: string]: any }[];
  // Study L (v0.55.0): per-window ENTRY/EOD rows + the 09:35 SCAN ladder
  // Study N (v0.67.0) reuses scan/entry_*/eod_* (call side) + Caleb's real
  // call cs that day, parsed from the strategy's OPG_TRADE_LOG.md
  caleb_real?: { short_strike?: number; long_strike?: number; entry?: string | null; credit?: number | null; exit_debit?: number | null; pnl_per_ct?: number | null; status?: string; approx?: boolean } | null;
  scan?: { ts_et?: string; spot?: number; call_wall?: number; ladder?: { dist?: number; short_strike?: number; credit?: number; credit_natural?: number; quotes_ok?: boolean }[] };
  entry_first?: { [k: string]: any };
  eod_first?: { [k: string]: any };
  entry_floor?: { [k: string]: any };
  eod_floor?: { [k: string]: any };
  // Study M (v0.66.0): chased-close START + resolution, the real concession
  // row for the same close, and the after-exit mid path (one/min)
  start?: { [k: string]: any };
  result?: { [k: string]: any };
  concession?: { [k: string]: any } | null;
  path?: { t: string; mid: number }[];
  // Study O (v0.67.0): the floored trade's own debit lines from the tracker
  lines?: { floor?: number | null; target?: number | null; stop?: number | null };
  queries?: { ts?: string; verdict?: string | null; confidence?: number; conf_threshold?: number; reason?: string; error?: string; breach_prob?: number | null; escalated?: boolean | null; interval_min?: number | null; misread?: string | null }[];
  real?: {
    trade?: { direction?: string; short_strike?: number; long_strike?: number; contracts?: number; entry_credit_per_share?: number } | null;
    exit_reason?: string | null;
    realized_pnl?: number | null;
    stand_aside?: string | null;
  };
}

interface ShadowsResp {
  days: number;
  dates_scanned: number;
  studies: {
    below_wall: StudyEntry[];
    exit_shadow: StudyEntry[];
    magnet_at_wall: StudyEntry[];
    wall_at_spot: StudyEntry[];
    giveback: StudyEntry[];
    ai_exit: StudyEntry[];
    sigma_put: StudyEntry[];
    hold_shadow: StudyEntry[];
    entry_risk: StudyEntry[];
    caleb_put: StudyEntry[];
    patient_close?: StudyEntry[];
    caleb_call?: StudyEntry[];
    profit_floor_ai?: StudyEntry[];
  };
  // R10 (v0.46.0, dark) thin-credit would-cap ledger — renders under the
  // Roadmap panel's R10 row, not as a study card
  r10_thin_credit?: {
    date: string;
    would_cap_from?: number | null;
    would_cap_to?: number | null;
    capped_from?: number | null;
    applied_factor?: number | null;
    real?: StudyEntry['real'];
  }[];
  // R12 (v0.49.0): wall-stability shadow — EOD row lands every day
  r12_wall_stability?: { date: string; eod?: { [k: string]: any }; real?: StudyEntry['real'] }[];
  timestamp: string;
}

// Static roster (from the prompt, edited on every release by the engine's
// maintainers — diff on each prompt update and prune graduated items).
const STUDY_META: {
  key: keyof ShadowsResp['studies'];
  name: string;
  status: 'SHADOW' | 'DARK — not armed' | 'MEASUREMENT';
  rule: string;
  decision: string;
}[] = [
  {
    key: 'below_wall',
    name: 'A · Below-wall put — OPG wall−2 template (v0.41.0)',
    status: 'MEASUREMENT',
    rule: "Records OPG's wall−2 put spread on negative mornings when it clears the credit floor, then replays our exits vs his (no wall exit) vs hold on 1-min bars.",
    decision: 'Arm when the HIGH bucket has ≈10 scored mornings and 0 stops under no_wall_exit.',
  },
  {
    key: 'exit_shadow',
    name: 'B · Exit-rule alternatives — stop/wall/double-touch (v0.40.0)',
    status: 'MEASUREMENT',
    rule: 'At every real stop/breach exit, replays alternative stop rules (floor_width, high_iv_mult, stop_confirm, wall_confirm, double_touch, wall_only, hold; confirm3_backstop + spot_through since v0.51.0) against what actually happened.',
    decision: 'No change to the 4× stop until the 10:15 window has produced its own stop-outs; judge on Σ AND worst day.',
  },
  // Study C (wall-strike PREFERENCE, dark v0.30.0) closed 10/15 — would_fire
  // never true over 7 leader entries; superseded by the early wall-strike put
  // (R3/R9). Code stays dark; wall_strike_preference rows are ignored here.
  {
    key: 'magnet_at_wall',
    name: 'D · Magnet-at-wall veto (v0.36.0)',
    status: 'SHADOW',
    rule: 'Negative-regime put mornings where the magnet sits on the put wall within 0.6% of spot — records the put taken/skipped and the call alternative. Since v0.52.0, mornings where the selector produced NO put candidate (usually the exit-cushion floor) are recorded too, with the wall-break timing.',
    decision: 'Candidate veto at 0.35% gap — compare Σ put P&L skipped vs Σ call-alt P&L taken on would_veto days.',
  },
  {
    key: 'wall_at_spot',
    name: 'E · Wall-at-spot lower exit line (v0.34.0)',
    status: 'SHADOW',
    rule: 'Mornings the exit-cushion floor (0.20%; 0.25% since v0.52.0) made us stand aside — replays the blocked spread under wall / alt-line / hold exit rules.',
    decision: 'Compare Σ per rule once enough cushion-blocked mornings accumulate.',
  },
  {
    key: 'giveback',
    name: 'G · Giveback exit — "don\'t give it back" (v0.44.0)',
    status: 'MEASUREMENT',
    rule: 'Once a trade has captured ≥ 33% of its credit, scratch it if it falls all the way back to breakeven — scored on every leader trade, fired or not, in always-on and fragile-only variants. The −50% variant (exit at 1.5× credit) was LIVE v0.54.0–v0.64.0 and is back to a shadow since 10/21 (G-live on the roadmap): the profit floor was catching the losers, the giveback only cut winners.',
    decision: 'Arm only if, after ~10 scored trades on the v0.43 stack, fragile-only Σ > actual Σ and its worst is no worse.',
  },
  {
    key: 'ai_exit',
    name: 'H · AI exit desk — "should we exit now?" (v0.45.0; v2 breach-prob since v0.48.0; v3 EV exit since v0.52.0)',
    status: 'MEASUREMENT',
    rule: 'A model is asked hold/exit every 5 minutes while the trade is open (every minute once the giveback shadow arms); since v0.48.0 each answer also carries breach_prob, scored by Brier. v3 (v0.52.0, prompt_version 3) adds an EV exit (first query where cutting at the mark beats the model\'s own p-weighted hold) and redefines breach_prob as "ends on a loss exit (line OR stop)" — v2 and v3+ Brier means are kept separate. v5 (v0.60.0, 10/20) adds a misread check: the model states the direction its answer rests on; a vote contradicting the code\'s direction is discarded (not scored, no live exit) and re-asked a minute later. The score, not the story, decides.',
    decision: 'Arm nothing from this table before ~10 scored trades; then the variant with the best Σ AND no worse worst-case than actual is the candidate — and only if it also beats giveback, the simpler rule.',
  },
  {
    key: 'sigma_put',
    name: 'I · σ put strike — Caleb\'s distance rule (v0.51.0, R13)',
    status: 'SHADOW',
    rule: 'Prices the put short by distance from spot (0.6 daily-σ, min 0.4%) instead of our GEX levels, $2 wide, off the live chain — recorded per window (early 09:35 / live 10:15) whether or not we trade; only POSITIVE/MIXED mornings count toward n.',
    decision: 'After ~10 scored POSITIVE/MIXED mornings, arm only if the live window beats what we actually traded on Σ AND worst morning; the early window is a separate question (it contradicts the 9/28 replay that moved us to 10:15) and is read last.',
  },
  {
    key: 'hold_shadow',
    name: 'J · In-trade shadows — bleed exit, early mixed (v0.52.0, R14/R16)',
    status: 'SHADOW',
    rule: 'While the leader holds: bleed exit fires when the mark holds ≥ 2.5× credit with spot within 0.15% of the exit line for 10 straight check-ins; early-mixed tags leader fills before 09:46 on a MIXED map. A shadow exit that fired after the real close counts as "never fired" (P&L = actual). Never in the P&L. (Magnet-hold, R15, closed 10/31 — its rows are ignored here; the in-trade map stays because it feeds the AI exits.)',
    decision: 'Arm nothing before ~10 fires per variant; a variant is a candidate only if Σ beats actual AND it scratched no more than one winner.',
  },
  {
    key: 'entry_risk',
    name: 'K · Entry risk — high-IV cap, session extreme (v0.53.0)',
    status: 'MEASUREMENT',
    rule: 'On every leader entry: the LIVE ×0.60 high-IV cap (R18b, ATM IV > 16.5%) shown on capped entries, and the session-extreme tag — how close the day\'s low/high came to the short strike. Data only, never a gate. (R17 IV margin and R18a IV-aware sizing closed 10/31 — their rows are ignored here.)',
    decision: 'Nothing left to arm here — R17 and R18a closed 10/31 (see the Closed list); the cap is LIVE.',
  },
  {
    key: 'caleb_put',
    name: 'L · Caleb-distance put (v0.55.0, R19)',
    status: 'SHADOW',
    rule: 'A 2-wide put ~0.65% below spot, scanned 09:35–10:15 in every regime, scored with Caleb\'s exits (rest at $0.03; stop only through the short or at 8× credit) — recorded whether or not we trade, with the live first-scan credit (mid AND natural) checked against the $0.08 floor. Grey, never in the P&L.',
    decision: '~10 mornings; arm only if the live credit is there (most first mids ≥ $0.08) AND Σ caleb beats our real trades with no worse worst day. Arming = a new live template, operator\'s word. First review 10/31 FAILED (10 mornings: Caleb\'s exits +9/ct, our 4× +14/ct, two losers vs 0 in the backtest; our real trades +29/ct) — not armed, still recording; closes if the next 10 mornings look the same.',
  },
  {
    key: 'patient_close',
    name: 'M · Patient profit close (v0.66.0, R20)',
    status: 'SHADOW',
    rule: 'When the leader\'s profit close fills ABOVE its resting limit (the ladder chased), a virtual order keeps resting at the target: filled once the mid < target; chase at mid + $0.01 once the mid ≥ target + $0.03; chase at target + $0.01 after 20 min or at 15:45. Saved = what we paid − what the shadow would have paid. Grey, never in the P&L.',
    decision: '~10 chased closes; arm (patient live close) only if Σ saved > 0 AND no single day worse than −3/ct. Arming is a LIVE exit change, operator\'s word.',
  },
  {
    key: 'caleb_call',
    name: 'N · Caleb-distance call (v0.67.0, R21)',
    status: 'SHADOW',
    rule: 'Study L\'s call-side twin: a 2-wide call ~0.65% above spot, scanned 09:35–10:45 in every regime, scored with Caleb\'s exits (rest at $0.03; stop when SPY closes through the short or at 8× credit) — recorded whether or not we trade, with the live first-scan credit (mid AND natural) checked against the $0.08 floor, beside Caleb\'s real call trade that day. Grey, never in the P&L.',
    decision: '~10 floor-clearing mornings; arm (a live call template) only if Σ beats our real trades with no worse worst day — operator\'s word.',
  },
  {
    key: 'profit_floor_ai',
    name: 'O · AI on the profit floor (v0.67.0, R22 — graduated v0.69.0)',
    status: 'SHADOW',
    rule: 'Graduated 10/30: the floor AI is LIVE (v0.69.0) and FLOOR_AI_CLOSE is its scoreboard (EOD tab) — these rows now only appear when the floor AI fell back to the fixed floor. Every time the leader\'s 35% → 30% profit floor fires, records the latest AI exit vote (verdict, confidence, breach_prob), then replays holding from the fire under our live exits (target, 4× stop, short / wall breach, 15:45). Variants: ai_veto (hold whenever the vote is HOLD) and bp_lt_x (hold only when HOLD and breach_prob < 0.10 / 0.15 / 0.20); a variant that holds takes the held P&L, otherwise the real floor P&L. Grey, never in the P&L.',
    decision: '~10 floor fires with a vote; a variant goes live only if its Σ beats the floor AND it never holds into a stop — operator\'s word.',
  },
];

const ROADMAP: { id: string; item: string; status: string; next: string; where: string }[] = [
  { id: 'R1', item: 'Stop multiple (4×) — width floor, IV-scaled multiple, confirmation counts', status: 'Backtested 10/12 (analysis/oct12_strike_stop_study/), no code', next: 'On our own strikes 9/14–10/9: 3-mark confirmation changes nothing (−54/ct), 5-mark +40, "4× AND spot through the short, 8× backstop" +29 (saves 9/25 + 9/28, costs ~$10/ct more on 9/23\'s real break, holds 4.5–5.5× marks). v0.51.0 (10/12): EXIT_SHADOW_EOD.alternatives now scores confirm3_backstop and spot_through on every real stop (study B columns), and study I scores confirm_backstop on the σ put. Next: ~10 real stops before arming; judge on Σ AND worst day', where: '§28, changelog 0.40.0, 0.51.0, Unreleased 10/12' },
  // R2 closed 10/3 (since v0.42/v0.43 the first entry is the 09:35 wall-strike
  // put and 10:15 is the fallback window — nothing left to decide) and
  // R3 graduated 9/30 (v0.42.0 early wall-strike put) — removed per roster discipline
  { id: 'R4', item: 'Selector ideas from the exit study — price-and-premium confirm (spot beyond wall AND mid ≥ 2× credit); re-test rule (exit on the second touch within N min)', status: 'Untested', next: 'Needs a replay script over the EXIT_SHADOW_FIRE rows once there are ~10', where: '§28' },
  // R5 closed 10/15 (see CLOSED list); R6 graduated 10/15 — became the
  // conviction ladder (LIVE since v0.22.0 phase 2, rungs v0.29.0); its open
  // refinements stay on the roster as R10 and R18
  { id: 'R7', item: 'SPX/XSP migration', status: 'Waiting on Alpaca', next: 'Watch item: assignment risk, 60/40 tax; re-check when Alpaca lists index options', where: '§17' },
  { id: 'R10', item: 'Thin-credit size cap (credit ≤ $0.10 → ladder capped at base ×1.0; risk.thin_credit_cap)', status: 'Built DARK v0.46.0 (10/6, operator "OFF mode for now")', next: 'Score CONVICTION_SHADOW.thin_credit_would_cap_* rows: P&L/ct × actual vs capped contracts over ~10 thin entries; ledger so far 8 thin trades 7W +12/ct worst −28 — a payoff-per-risk rule, not a win-rate one. Arm with GAMMA_CONVICTION_THIN_CREDIT_CAP=1.0', where: 'changelog 0.46.0' },
  // R11 (profit-close patience until 14:00) graduated off the roster 10/15 —
  // LIVE since v0.50.0; PROFIT_CLOSE_CONCESSION rows are Exit-reason-view
  // calibration now, not a shadow
  // R12 graduated 10/16 — the mode-5 entry gate went LIVE v0.54.0 (operator
  // "move from shadow to LIVE"; replay blocked 5 of 26, leader +$4.3k).
  // WALL_STABILITY_BLOCK + NO_SPREAD wall_unstable_mode5_* are stand-aside
  // reasons now; the shadow keeps recording the run3 variant (panel below)
  { id: 'R13', item: 'σ-anchored put strike (0.6 daily-σ) on POSITIVE/MIXED — short = highest strike ≤ spot × (1 − max(0.6σ, 0.4%)), replacing the flip / beyond-the-put-wall templates on the put side', status: 'Shadow built v0.51.0 (10/12, operator "build the shadow"; live from the next restart)', next: 'Backtest: Caleb\'s put shorts sat 0.42–0.71σ below spot (median 0.63σ, 13 trades). In the 10:15 window sig60 scores +38/ct vs −54 realized (18 trades, 3 losses; adds 9/22, 9/30, 10/9; flips 9/25 + 9/28 to wins; 10/2 −30 vs −15). NEGATIVE mornings excluded (all-regime −14). Now: score study I (SIGMA_PUT_SHADOW_EOD, early + live windows) over ~10 POSITIVE/MIXED mornings against our real trades; arm only on Σ AND worst morning', where: 'changelog 0.51.0, Unreleased 10/12' },
  { id: 'R14', item: 'Bleed exit — exit once the mark ≥ 2.5× credit with spot within 0.15% of the exit line for 10 straight check-ins (the 10/14 "pinned above the wall, bled to the stop" shape); gamma/hold_shadow.py', status: 'Shadow built v0.52.0 (10/14, operator "lets shadow test the above"; live from the next restart)', next: 'Replay 8/24–10/14 (analysis/oct14_trade_lessons/): +22/ct over 32 trades (9/23 −29 → −17, 10/14 −36 → −26), no winner touched; 3× stop +8, 2.5× stop −18 (clip winners). Score study J (HOLD_SHADOW_EOD.bleed_*) over ~10 fires', where: 'MULTI_ACCOUNT §35, changelog 0.52.0' },
  { id: 'R16', item: 'Early entry on a MIXED map — would block leader fills before 09:46 when the map is MIXED', status: 'Shadow built v0.52.0 (10/14)', next: 'Ledger 8/24–10/13: 6 trades, 3 losses, −76/ct (all other entries +124/ct over 26). Score study J (early_mixed) — arming it is a LIVE change and needs the operator\'s word', where: 'MULTI_ACCOUNT §35, changelog 0.52.0' },
  { id: 'R19', item: 'Caleb-distance put — a 2-wide put ~0.65% below spot, 09:35–10:15, every regime, with Caleb\'s exits (rest $0.03, stop only through the short or at 8× credit); gamma/caleb_put_shadow.py', status: 'Shadow built v0.55.0 (10/16, operator "yes build") — FAILED first review 10/31 (10 mornings 10/19–10/30: Caleb\'s exits +9/ct, our 4× +14/ct, two losers −62 (10/22) / −53 (10/30) vs the backtest\'s 0 in 31; our real trades +29/ct over 8 of those days). Not armed; operator 10/31: keep recording, close if the next 10 mornings look the same', next: 'Replay 8/24–10/16 on bar closes: our strikes + his exits −8/ct; 0.65% put @09:35 + his exits +189/ct, 0 losers over 31 (+145 over 17 at the $0.08 floor); Caleb +161 over 27. Score study L: live first credit at 0.65% vs $0.08 (mid AND natural), rules.caleb vs rules.live_4x vs our real trade, over ~10 mornings. Arming = a new live template, operator\'s word', where: 'changelog 0.55.0' },
  { id: 'R20', item: 'Patient profit close — when the leader\'s profit close is chased past its resting limit, keep a virtual order at the target: filled once the mid < target, chase at mid + $0.01 once the mid ≥ target + $0.03, chase after 20 min or at 15:45; gamma/patient_close_shadow.py', status: 'Shadow built v0.66.0 (10/26, operator "lets build it in shadow mode")', next: 'Slippage review 10/26: 22 of 23 leader profit closes since 8/24 chased past the target (avg $1.32/ct). Replay of the exact rule on 17 chased closes: +15/ct, +$525 leader, 3 worse (a +2c guard breaks even — print noise). Score study M over ~10 chased closes; arm only if Σ saved > 0 and no day worse than −3/ct. Arming is a LIVE exit change, operator\'s word', where: 'changelog 0.66.0' },
  { id: 'R21', item: 'Caleb-distance call — R19\'s twin: a 2-wide call ~0.65% above spot, 09:35–10:45, every regime, Caleb\'s exits (rest $0.03, stop when SPY closes through the short or at 8×); gamma/caleb_call_shadow.py', status: 'Shadow built v0.67.0 (10/26, operator "the more data the better … lets build it")', next: 'Built after his 768/770 @ $0.15 (09:49) on a morning our call fade was blocked by the 0.30% cushion (spot 0.20% under the 765 wall). His call shorts sit 0.61–0.70% over spot. Replay 8/24–10/23: 12 of 44 mornings ≥ $0.08 → his exits +31/ct (1 loser −64), our 4× +66/ct; +55/ct on the 5 days we sat out. Score study N over ~10 floor-clearing mornings; arming = a new live template, operator\'s word', where: 'changelog 0.67.0' },
  // R15, R17, R18a closed 10/31 (see CLOSED)
  // R22 graduated 10/30 — LIVE floor AI v0.69.0 (floor_ai_exit; scoreboard on the EOD tab)
  { id: 'H-ev', item: 'AI exit, cost-benefit ("ev") variant — exit at the first query where cutting beats the model\'s own p-weighted hold value. (The high-confidence variant went LIVE v0.54.0)', status: 'Shadow (v0.52.0)', next: '2 scored trades: 10/16 +4 vs −49, 10/15 +3 vs +11 (paper +18). Keep scoring AI_EXIT_SHADOW_EOD.ai_ev_*', where: 'changelog 0.52.0, 0.54.0' },
  // v0.64.0 (10/21): the LIVE giveback exit (v0.54.0) was demoted back to a
  // shadow — the profit floor was catching the losers and the giveback was
  // only cutting winners
  { id: 'G-live', item: '−50% giveback exit, back to shadow — armed at 33% captured, would exit when the mark is back at 1.5× credit (−50%). Leader-only GIVEBACK_LIVE_SHADOW row (once per trade) where it would have fired', status: 'Shadow (v0.64.0, 10/21 — was LIVE v0.54.0)', next: 'Replay 8/24–10/21 with the profit floor: 4 fires, all winners cut; without it +71/ct. Re-arm via GAMMA_GIVEBACK_LIVE=1 only if the floor stops catching the losers', where: '—' },
];

// Analyzed and NOT adopted — short collapsed list under the Roadmap panel.
const CLOSED: { item: string; closed: string; verdict: string }[] = [
  {
    item: 'R15 Magnet-hold exit (MAGNET_HOLD_SHADOW, exit when the magnet sits on/through the exit line)',
    closed: '10/31',
    verdict: '3 fires 10/20–10/27: −13, 0, −10/ct — cut two winners, saved nothing. The in-trade map (HOLD_MAP) stays: it feeds the AI exit\'s and the floor AI\'s map_now. Shadow rows may still appear — ignored on this tab',
  },
  {
    item: 'R17 Early wall-strike put IV margin (IV_MARGIN_SHADOW, 18.5% cap / 5-scan run / ≥ 0.40σ)',
    closed: '10/31',
    verdict: '4 high-IV mornings: would have blocked 10/19 (+9), 10/20 (+9), 10/21 (−9) → −9/ct net, and passed 10/16 (−49). Shadow rows may still appear — ignored',
  },
  {
    item: 'R18a IV-aware sizing (IV rung fails near the 20% cap, tier_if_early_iv_aware, dark)',
    closed: '10/31',
    verdict: 'Superseded by the live ×0.60 high-IV cap (R18b): on 10/20 and 10/21 the IV-aware tier (0.8) was above the size actually traded (0.6). Stays dark',
  },
  {
    item: 'R8 Premium-efficiency sizing (∝ 1/credit, Caleb\'s rule)',
    closed: '9/30',
    verdict: 'Not supported: our three 4× stops sold thin credits ($0.09–0.11), so ∝1/c sizing puts MORE contracts on the stop days — worst day −$4,066 → −$4,788, stop-day Σ −$10,993 → −$13,333, Σ P&L +$3,648 → +$683 (22 real trades). §30',
  },
  {
    item: 'R5 Call-side / high-volatility book',
    closed: '10/15',
    verdict: 'Never left brainstorm; the call-wall fade already covers the call side, and the ledger argues the opposite direction — IV > 16.5%: 7 trades, 3 losses, −5.7/ct (§36) → size DOWN in high IV (R18), not a separate book',
  },
  {
    item: 'Study C — wall-strike PREFERENCE (dark since v0.30.0, §21)',
    closed: '10/15',
    verdict: 'Recorded on 7 leader entries, would_fire never true; superseded by the early wall-strike put (R3/R9), which sells the wall strike itself from 09:35. Code stays dark (GAMMA_WALL_STRIKE_PREFERENCE_ARMED off); gex_at_entry.wall_strike_preference may still appear — ignore it on this tab',
  },
];

// confirm3_backstop / spot_through are v0.51.0 (R1 backtest) — absent on
// rows written before 10/12, rendered "—"
const EXIT_ALT_NAMES = ['live_rule', 'floor_width', 'high_iv_mult', 'stop_confirm', 'wall_confirm', 'double_touch', 'confirm3_backstop', 'spot_through', 'wall_only', 'hold'];

const fmt$ = (v: number | null | undefined, digits = 0): string =>
  v == null || isNaN(Number(v)) ? '—' : `${v < 0 ? '−' : '+'}$${Math.abs(Number(v)).toFixed(digits)}`;
const fmtTime = (ts?: string | null): string => (ts ? ts.slice(11, 16) : '—');

// Real-trade context cell: "shadow vs reality side by side"
const RealCell: React.FC<{ real?: StudyEntry['real'] }> = ({ real }) => {
  if (!real) return <>—</>;
  if (real.trade) {
    return (
      <>
        {real.trade.short_strike}/{real.trade.long_strike}{real.trade.direction === 'call_credit' ? 'c' : 'p'}
        {' '}· {real.exit_reason || 'open'}{' '}
        <span style={{ fontWeight: 600 }}>{fmt$(real.realized_pnl)}</span>{' '}
        <Chip label="REAL" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
      </>
    );
  }
  return <span style={{ opacity: 0.7 }}>no trade{real.stand_aside ? ` (${String(real.stand_aside).split('|')[0].slice(0, 32)}…)` : ''}</span>;
};

const statusChip = (s: string) => (
  <Chip
    label={s}
    size="small"
    variant="outlined"
    color={s === 'SHADOW' ? 'info' : s.startsWith('DARK') ? 'default' : 'secondary'}
    sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
  />
);

const ShadowsView: React.FC<{ instanceName: string }> = ({ instanceName }) => {
  const { token } = useAuth();
  const [data, setData] = useState<ShadowsResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [studyFilter, setStudyFilter] = useState<string[]>([]);
  const [aiExpanded, setAiExpanded] = useState<string | null>(null);
  // Study I window toggle — 'live' (10:15, our current window) is the default
  const [sigmaWindow, setSigmaWindow] = useState<'live' | 'early'>('live');
  // Study L expanded row (09:35 SCAN ladder strip), keyed on date
  const [lExpanded, setLExpanded] = useState<string | null>(null);
  // Study M expanded row (after-exit mid path chart), keyed on account|opened_at
  const [mExpanded, setMExpanded] = useState<string | null>(null);
  // Study N expanded row (09:35 call SCAN ladder strip), keyed on date
  const [nExpanded, setNExpanded] = useState<string | null>(null);
  // Study O expanded row (after-fire mid path chart), keyed on account|opened_at
  const [oExpanded, setOExpanded] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`/api/instances/${instanceName}/opt-spy-shadows?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e: any) {
      setError(e.message || 'failed to load shadows feed');
    } finally {
      setLoading(false);
    }
  }, [instanceName, token, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const visible = useMemo(() => {
    const filt = (entries: StudyEntry[]) =>
      (entries || []).filter((e) => !pendingOnly || (e.record && !e.eod) || (e.fire && !e.eod));
    return {
      below_wall: filt(data?.studies.below_wall || []),
      exit_shadow: filt(
        // leader rows by default (account toggle deliberately deferred until
        // follower divergence matters — sleeves share the decision)
        (data?.studies.exit_shadow || []).filter((e) => !e.account || e.account.startsWith('Ilan'))
      ),
      magnet_at_wall: filt(data?.studies.magnet_at_wall || []),
      wall_at_spot: filt(data?.studies.wall_at_spot || []),
      // G/H: the EOD row lands on every leader trade — pending = no EOD yet
      giveback: (data?.studies.giveback || []).filter((e) => !pendingOnly || !e.eod),
      ai_exit: (data?.studies.ai_exit || []).filter((e) => !pendingOnly || !e.eod),
      sigma_put: (data?.studies.sigma_put || []).filter((e) => !pendingOnly || !e.eod),
      hold_shadow: (data?.studies.hold_shadow || []).filter((e) => !pendingOnly || !e.eod),
      // K: the CLOSE row is the score; candidate-only entries never close
      entry_risk: (data?.studies.entry_risk || []).filter((e) => !pendingOnly || (e.open && !e.close)),
      // L: the EOD row (window 'first') is the score
      caleb_put: (data?.studies.caleb_put || []).filter((e) => !pendingOnly || !e.eod_first),
      // M: the PATIENT_CLOSE_SHADOW row is the score; START-only = pending/unresolved
      patient_close: (data?.studies.patient_close || []).filter((e) => !pendingOnly || !e.result),
      // N: same as L — the 'first' EOD row is the score
      caleb_call: (data?.studies.caleb_call || []).filter((e) => !pendingOnly || !e.eod_first),
      // O: the PROFIT_FLOOR_AI_SHADOW (EOD) row is the score
      profit_floor_ai: (data?.studies.profit_floor_ai || []).filter((e) => !pendingOnly || !e.result),
    };
  }, [data, pendingOnly]);

  const counts: { [k: string]: number } = {
    below_wall: visible.below_wall.length,
    exit_shadow: visible.exit_shadow.length,
    magnet_at_wall: visible.magnet_at_wall.length,
    wall_at_spot: visible.wall_at_spot.length,
    giveback: visible.giveback.length,
    ai_exit: visible.ai_exit.length,
    // Study I: only POSITIVE/MIXED mornings count toward n (live window)
    sigma_put: visible.sigma_put.filter((e) => {
      const reg = (e.eod?.regime || e.record?.regime || '').toLowerCase();
      return e.window === 'live' && e.eod?.entered !== false && reg !== 'negative';
    }).length,
    hold_shadow: visible.hold_shadow.filter((e) => e.eod).length,
    entry_risk: visible.entry_risk.filter((e) => e.open).length,
    caleb_put: visible.caleb_put.filter((e) => e.eod_first).length,
    patient_close: visible.patient_close.filter((e) => e.result).length,
    caleb_call: visible.caleb_call.filter((e) => e.eod_first).length,
    profit_floor_ai: visible.profit_floor_ai.filter((e) => e.result).length,
  };

  // Study A decision pill: green when HIGH bucket has ≥10 scored mornings and
  // 0 stops under no_wall_exit; amber while accumulating; grey when empty.
  const pillFor = (key: string): 'success' | 'warning' | 'default' => {
    if (!counts[key]) return 'default';
    if (key === 'below_wall') {
      const scoredHigh = visible.below_wall.filter((e) => e.eod && (e.eod.iv_bucket === 'high'));
      const stops = scoredHigh.filter((e) => (e.eod?.rules?.no_wall_exit?.exit || '') === 'max_loss_close');
      return scoredHigh.length >= 10 && stops.length === 0 ? 'success' : 'warning';
    }
    return 'warning';
  };

  if (loading && !data) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {/* Header + filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Shadow studies &amp; roadmap</Typography>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Range</InputLabel>
            <Select value={days} label="Range" onChange={(e) => setDays(Number(e.target.value))}>
              <MenuItem value={14}>14 days</MenuItem>
              <MenuItem value={30}>30 days</MenuItem>
              <MenuItem value={60}>60 days</MenuItem>
              <MenuItem value={120}>120 days</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Studies</InputLabel>
            <Select
              multiple
              value={studyFilter}
              label="Studies"
              onChange={(e) => setStudyFilter(typeof e.target.value === 'string' ? [e.target.value] : e.target.value)}
              renderValue={(sel) => (sel.length ? `${sel.length} selected` : 'all')}
            >
              {STUDY_META.map((s) => <MenuItem key={s.key} value={s.key}>{s.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch size="small" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />}
            label={<Typography variant="caption">pending only</Typography>}
          />
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          Data only — no shadow ever traded, nothing here is in the P&L, and no cell is a recommendation. All shadow P&L is
          per contract, replayed on 1-min bars; real figures carry a REAL tag. Scanned {data?.dates_scanned ?? 0} sessions.
        </Typography>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 1. Top strip — one card per study */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        {STUDY_META.filter((s) => !studyFilter.length || studyFilter.includes(s.key)).map((s) => (
          <Paper key={s.key} sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{s.name}</Typography>
              <Chip size="small" label={counts[s.key]} color={pillFor(s.key)} sx={{ fontWeight: 'bold' }} />
            </Box>
            <Box sx={{ my: 0.5 }}>{statusChip(s.status)}</Box>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{s.rule}</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              Decision rule: {s.decision}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* 2. Expandable per-study tables */}
      {(!studyFilter.length || studyFilter.includes('below_wall')) && (
        <Accordion defaultExpanded={counts.below_wall > 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>A · Below-wall put (OPG wall−2) — {counts.below_wall} morning(s)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {counts.below_wall === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No rows in range.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'IV bucket', 'Shadow spread', 'Credit', 'Recorded', 'Scans under floor', 'Put wall', 'Min under wall', 'Short touched', 'Our exits (per ct)', 'His exits (per ct)', 'Hold (per ct)', 'Last print', 'Real trade'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.below_wall.map((e) => {
                      const r = e.record || {}; const d = e.eod || {};
                      const rules = d.rules || {};
                      const ours = rules.our_exits || {}; const his = rules.no_wall_exit || {}; const hold = rules.hold || {};
                      return (
                        <TableRow key={e.date} sx={{ opacity: e.eod ? 1 : 0.6 }}>
                          <TableCell>{e.date}{!e.eod && <Chip label="pending" size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}</TableCell>
                          <TableCell>{(d.iv_bucket || r.iv_bucket || '—')}{r.atm_iv != null ? ` (${(r.atm_iv * 100).toFixed(1)}%)` : ''}</TableCell>
                          <TableCell>{r.short_strike ?? d.short_strike}/{r.long_strike ?? d.long_strike}p</TableCell>
                          <TableCell>${Number(r.credit ?? d.credit ?? 0).toFixed(2)}</TableCell>
                          <TableCell>{fmtTime(r.ts_et || d.recorded_at)}</TableCell>
                          <TableCell>{r.scans_before ?? '—'}</TableCell>
                          <TableCell>{r.put_wall ?? d.put_wall ?? '—'}</TableCell>
                          <TableCell>{d.minutes_under_wall ?? '—'}</TableCell>
                          <TableCell>{d.short_touched ? <span style={{ color: '#ef5350' }}>●</span> : 'no'}</TableCell>
                          <TableCell>{ours.exit ? `${ours.exit} @ ${fmtTime(ours.at)} ${fmt$(ours.pnl_per_ct)}` : '—'}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{his.exit ? `${his.exit} @ ${fmtTime(his.at)} ${fmt$(his.pnl_per_ct)}` : '—'}</TableCell>
                          <TableCell>{fmt$(hold.pnl_per_ct)}</TableCell>
                          <TableCell>{d.spread_last_print != null ? `$${Number(d.spread_last_print).toFixed(2)}` : '—'}</TableCell>
                          <TableCell><RealCell real={e.real} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {counts.below_wall > 0 && (() => {
              // footer per IV bucket: n · W/L under his exits · Σ his vs ours · stops under his
              const scored = visible.below_wall.filter((e) => e.eod);
              const buckets = ['high', 'low'].map((b) => {
                const rows = scored.filter((e) => (e.eod!.iv_bucket || '') === b);
                const his = rows.map((e) => e.eod!.rules?.no_wall_exit || {});
                const ours = rows.map((e) => e.eod!.rules?.our_exits || {});
                return {
                  b, n: rows.length,
                  w: his.filter((h) => (h.pnl_per_ct ?? 0) > 0).length,
                  l: his.filter((h) => (h.pnl_per_ct ?? 0) <= 0).length,
                  sumHis: his.reduce((s, h) => s + (h.pnl_per_ct || 0), 0),
                  sumOurs: ours.reduce((s, o) => s + (o.pnl_per_ct || 0), 0),
                  stops: his.filter((h) => h.exit === 'max_loss_close').length,
                };
              }).filter((x) => x.n > 0);
              return buckets.length ? (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  {buckets.map((x) => `${x.b.toUpperCase()} bucket: n=${x.n} · ${x.w}W/${x.l}L under his exits · Σ his ${fmt$(x.sumHis)}/ct vs ours ${fmt$(x.sumOurs)}/ct · stops under his: ${x.stops}`).join('   ·   ')}
                  {'  '}(per contract, replayed on 1-min bars)
                </Typography>
              ) : null;
            })()}
          </AccordionDetails>
        </Accordion>
      )}

      {(!studyFilter.length || studyFilter.includes('exit_shadow')) && (
        <Accordion defaultExpanded={counts.exit_shadow > 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>B · Exit-rule alternatives — {counts.exit_shadow} stopped trade(s), leader</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {counts.exit_shadow === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No stop / wall / flip / short-strike exits in range (only such days appear here).
              </Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'Exit', 'Credit', 'Actual debit', 'Actual P&L', 'Stop shown for', 'Wall shown for', 'Spot vs level at fire', 'Level broken by close', 'Finished worthless', ...EXIT_ALT_NAMES].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h.replace(/_/g, ' ')}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.exit_shadow.map((e) => {
                      const f = e.fire || {}; const d = e.eod || {};
                      const alts = d.alternatives || {};
                      // calibration check: live_rule should ≈ actual within a few $/ct
                      const cal = alts.live_rule || {};
                      const calOff = cal.pnl_total != null && d.actual_pnl != null && d.contracts
                        ? Math.abs((cal.pnl_total - d.actual_pnl) / d.contracts) > 5 : false;
                      return (
                        <TableRow key={`${e.account}|${d.opened_at || f.opened_at || e.date}`} sx={{ opacity: e.eod ? 1 : 0.6, bgcolor: calOff ? 'rgba(255,183,77,0.08)' : undefined }}>
                          <TableCell>{e.date}{!e.eod && <Chip label="pending" size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}{calOff && <MuiTooltip title="live_rule drifted >$5/ct from the real result — the replay is off, not the rule"><Chip label="cal" size="small" color="warning" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} /></MuiTooltip>}</TableCell>
                          <TableCell><Chip label={(d.exit_reason || f.exit_reason || '?').replace(/_/g, ' ')} size="small" sx={{ fontSize: '0.6rem' }} /></TableCell>
                          <TableCell>${Number(d.credit ?? f.credit ?? 0).toFixed(2)}</TableCell>
                          <TableCell>${Number(d.actual_debit ?? 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: (d.actual_pnl ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                            {fmt$(d.actual_pnl)} <Chip label="REAL" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
                          </TableCell>
                          <TableCell>{f.stop_run_checkins != null ? `${f.stop_run_checkins} checkin(s)` : '—'}</TableCell>
                          <TableCell>{f.wall_run_checkins != null ? `${f.wall_run_checkins} checkin(s)` : '—'}</TableCell>
                          <TableCell>{f.level_dist_pts != null ? `${f.level_dist_pts > 0 ? '+' : ''}${f.level_dist_pts} pts` : '—'}</TableCell>
                          <TableCell>{d.level_broken_at_close == null ? '—' : d.level_broken_at_close ? 'YES' : 'no'}</TableCell>
                          <TableCell>{d.finished_worthless == null ? '—' : d.finished_worthless ? 'YES' : 'no'}</TableCell>
                          {EXIT_ALT_NAMES.map((name) => {
                            const a = alts[name];
                            if (!a) return <TableCell key={name}>—</TableCell>;
                            const delta = a.delta_vs_actual;
                            return (
                              <TableCell key={name} sx={{ whiteSpace: 'nowrap' }}>
                                {a.exit ? `${String(a.exit).replace(/_/g, ' ')}@${fmtTime(a.at)}` : '—'}{' '}
                                {fmt$(a.pnl_total)}
                                {delta != null && <span style={{ opacity: 0.65 }}> (Δ{fmt$(delta)})</span>}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {counts.exit_shadow > 0 && (() => {
              const scored = visible.exit_shadow.filter((e) => e.eod);
              if (!scored.length) return null;
              const sums = EXIT_ALT_NAMES.map((name) => {
                const vals = scored.map((e) => e.eod!.alternatives?.[name]?.pnl_total).filter((v: any) => v != null);
                return { name, sum: vals.reduce((s: number, v: number) => s + v, 0), worst: vals.length ? Math.min(...vals) : null };
              });
              const actual = scored.reduce((s, e) => s + (e.eod!.actual_pnl || 0), 0);
              return (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  Σ actual (REAL): {fmt$(actual)} · {sums.map((s) => `${s.name.replace(/_/g, ' ')}: Σ ${fmt$(s.sum)} / worst day ${fmt$(s.worst)}`).join(' · ')} — the tail is the point.
                </Typography>
              );
            })()}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Study C accordion removed 10/15 — closed (see the CLOSED list):
          would_fire never true over 7 leader entries; superseded by the
          early wall-strike put (R3/R9) */}

      {(!studyFilter.length || studyFilter.includes('magnet_at_wall')) && (
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>D · Magnet-at-wall — {counts.magnet_at_wall} morning(s)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {counts.magnet_at_wall === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No magnet-at-wall mornings in range.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'Spot', 'Put wall', 'Magnet', 'Gap %', 'Would veto (0.35%)', 'Put (recorded)', 'Put result (per ct)', 'Call alt', 'Call alt result (per ct)', 'Real trade'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.magnet_at_wall.map((e) => {
                      const r = e.record || {}; const d = e.eod || {};
                      const put = d.put || {}; const alt = d.call_alt || null;
                      return (
                        <TableRow key={e.date} sx={{ opacity: e.eod ? 1 : 0.6 }}>
                          <TableCell>{e.date}{!e.eod && <Chip label="pending" size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}</TableCell>
                          <TableCell>{r.spot ?? '—'}</TableCell>
                          <TableCell>{r.put_wall ?? '—'}</TableCell>
                          <TableCell>{r.magnet ?? '—'}</TableCell>
                          <TableCell>{r.wall_gap_pct ?? '—'}</TableCell>
                          <TableCell>{r.would_veto ? 'YES' : 'no'}</TableCell>
                          {/* v0.52.0: put: null + put_reason "no_candidate" = the selector
                              produced no put (usually the exit-cushion floor) */}
                          <TableCell>{r.put ? `${r.put.short_strike}/${r.put.long_strike} @ $${Number(r.put.credit ?? 0).toFixed(2)}` : (r.put_reason === 'no_candidate' ? 'no put (blocked)' : '—')}</TableCell>
                          <TableCell>{put.exit ? `${put.exit} @ ${fmtTime(put.t)} ${fmt$(put.pnl_per_ct)}` : '—'}</TableCell>
                          <TableCell>{r.call_alt ? `${r.call_alt.short_strike ?? '?'}/${r.call_alt.long_strike ?? '?'}` : (r.call_alt_reason || 'none')}</TableCell>
                          <TableCell>{alt?.exit ? `${alt.exit} @ ${fmtTime(alt.t)} ${fmt$(alt.pnl_per_ct)}` : '—'}</TableCell>
                          <TableCell><RealCell real={e.real} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {counts.magnet_at_wall > 0 && (() => {
              const veto = visible.magnet_at_wall.filter((e) => e.eod && e.record?.would_veto);
              if (!veto.length) return null;
              const sumPut = veto.reduce((s, e) => s + (e.eod!.put?.pnl_per_ct || 0), 0);
              const sumAlt = veto.reduce((s, e) => s + (e.eod!.call_alt?.pnl_per_ct || 0), 0);
              return (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  On would-veto days: Σ put {fmt$(sumPut)}/ct (what the veto would have skipped) vs Σ call-alt {fmt$(sumAlt)}/ct (what it would have taken instead).
                </Typography>
              );
            })()}
          </AccordionDetails>
        </Accordion>
      )}

      {(!studyFilter.length || studyFilter.includes('wall_at_spot')) && (
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>E · Wall-at-spot lower exit line — {counts.wall_at_spot} morning(s)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {counts.wall_at_spot === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No cushion-blocked mornings in range.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'Would-be spread', 'Credit', 'Cushion %', 'Alt line', 'Wall breached', 'Alt line breached', 'Short breached', 'rule_wall (per ct)', 'rule_alt_line (per ct)', 'rule_hold (per ct)', 'Real trade'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.wall_at_spot.map((e) => {
                      const r = e.record || {}; const d = e.eod || {};
                      const cell = (rule: any) => (rule?.exit ? `${rule.exit} @ ${fmtTime(rule.t)} ${fmt$(rule.pnl_per_ct)}` : '—');
                      return (
                        <TableRow key={e.date} sx={{ opacity: e.eod ? 1 : 0.6 }}>
                          <TableCell>{e.date}{!e.eod && <Chip label="pending" size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}</TableCell>
                          <TableCell>{r.short_strike}/{r.long_strike}p</TableCell>
                          <TableCell>${Number(r.credit ?? 0).toFixed(2)}</TableCell>
                          <TableCell>{r.cushion_pct ?? '—'}</TableCell>
                          <TableCell>{r.alt_line ?? '—'}</TableCell>
                          <TableCell>{fmtTime(d.wall_breached_at) }</TableCell>
                          <TableCell>{fmtTime(d.alt_line_breached_at)}</TableCell>
                          <TableCell>{fmtTime(d.short_breached_at)}</TableCell>
                          <TableCell>{cell(d.rule_wall)}</TableCell>
                          <TableCell>{cell(d.rule_alt_line)}</TableCell>
                          <TableCell>{cell(d.rule_hold)}</TableCell>
                          <TableCell><RealCell real={e.real} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {counts.wall_at_spot > 0 && (() => {
              const scored = visible.wall_at_spot.filter((e) => e.eod);
              if (!scored.length) return null;
              const sum = (k: string) => scored.reduce((s, e) => s + ((e.eod as any)?.[k]?.pnl_per_ct || 0), 0);
              return (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  Σ per rule: wall {fmt$(sum('rule_wall'))}/ct · alt line {fmt$(sum('rule_alt_line'))}/ct · hold {fmt$(sum('rule_hold'))}/ct.
                </Typography>
              );
            })()}
          </AccordionDetails>
        </Accordion>
      )}

      {(!studyFilter.length || studyFilter.includes('giveback')) && (
        <Accordion defaultExpanded={counts.giveback > 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>G · Giveback exit ("don't give it back") — {counts.giveback} scored trade(s), leader</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {counts.giveback === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No scored leader trades in range (rows start with v0.44.0, 10/2).</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'Credit', 'Exit', 'Actual P&L/ct', 'Peak captured', 'Fired', '−50% would-fire (G-live)', 'Always-on (/ct)', 'Fragile-only (/ct)', 'Fragile flags', 'Δ vs actual'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.giveback.map((e) => {
                      const d = e.eod || {}; const r = e.record || {};
                      const flags = [
                        { on: d.cushion_rung_fail ?? r.cushion_rung_fail, label: 'cushion FAIL' },
                        { on: d.iv_above_low_cap ?? r.iv_above_low_cap, label: 'IV > 16.5%' },
                        { on: d.fallback_entry ?? r.fallback_entry, label: 'fallback entry' },
                      ];
                      const delta = d.shadow_minus_actual_per_ct;
                      return (
                        <TableRow key={e.date} sx={{ opacity: e.eod ? 1 : 0.6 }}>
                          <TableCell>{e.date}{!e.eod && <Chip label="pending" size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}</TableCell>
                          <TableCell>${Number(d.credit ?? r.entry_credit_per_share ?? 0).toFixed(2)}</TableCell>
                          <TableCell><Chip label={(d.exit_reason || '?').replace(/_/g, ' ')} size="small" sx={{ fontSize: '0.6rem' }} /></TableCell>
                          <TableCell sx={{ fontWeight: 600, color: (d.actual_pnl_per_ct ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                            {fmt$(d.actual_pnl_per_ct)} <Chip label="REAL" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
                          </TableCell>
                          <TableCell>{(d.peak_captured ?? r.peak_captured) != null ? `${(((d.peak_captured ?? r.peak_captured) as number) * 100).toFixed(0)}%` : '—'}</TableCell>
                          <TableCell>{(d.fired ?? !!r.fired_at) ? `YES @ ${fmtTime(d.fired_at || r.fired_at)}${(d.would_exit_debit ?? r.would_exit_debit) != null ? ` ($${Number(d.would_exit_debit ?? r.would_exit_debit).toFixed(2)})` : ''}` : 'no'}</TableCell>
                          {/* v0.64.0 G-live: the demoted −50% live variant, back to
                              shadow — GIVEBACK_LIVE_SHADOW fires once per trade;
                              '—' before 10/21 (it was LIVE then, see exit reason) */}
                          <TableCell>
                            {e.live_fire
                              ? `YES @ ${fmtTime((e.live_fire as any).fired_at || (e.live_fire as any).ts_et)}${(e.live_fire as any).would_exit_debit != null ? ` ($${Number((e.live_fire as any).would_exit_debit).toFixed(2)})` : ''}`
                              : e.eod ? 'no' : '—'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{fmt$(d.shadow_pnl_per_ct)}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{fmt$(d.fragile_only_pnl_per_ct)}</TableCell>
                          <TableCell>
                            {flags.map((fl) => (
                              <MuiTooltip key={fl.label} title={fl.label}>
                                <span style={{ color: fl.on ? '#ffb74d' : 'rgba(128,128,128,0.35)', marginRight: 3 }}>●</span>
                              </MuiTooltip>
                            ))}
                          </TableCell>
                          <TableCell sx={{ color: (delta ?? 0) > 0 ? 'success.main' : 'text.secondary' }}>{fmt$(delta)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {(() => {
              const scored = visible.giveback.filter((e) => e.eod);
              if (!scored.length) return null;
              const col = (k: string) => scored.map((e) => (e.eod as any)[k]).filter((v: any) => v != null);
              const sum = (vs: number[]) => vs.reduce((s, v) => s + v, 0);
              const worst = (vs: number[]) => (vs.length ? Math.min(...vs) : null);
              const variantStats = (k: string) => {
                const saves = scored.filter((e) => (e.eod!.actual_pnl_per_ct ?? 0) < 0 && ((e.eod as any)[k] ?? 0) >= 0).length;
                const scratches = scored.filter((e) => (e.eod!.actual_pnl_per_ct ?? 0) > 0 && ((e.eod as any)[k] ?? 0) <= 0).length;
                return `${saves} save(s) / ${scratches} scratch(es)`;
              };
              return (
                <>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    n={scored.length} · Σ actual (REAL) {fmt$(sum(col('actual_pnl_per_ct')))}/ct (worst {fmt$(worst(col('actual_pnl_per_ct')))}) ·
                    Σ always-on {fmt$(sum(col('shadow_pnl_per_ct')))}/ct (worst {fmt$(worst(col('shadow_pnl_per_ct')))}, {variantStats('shadow_pnl_per_ct')}) ·
                    Σ fragile-only {fmt$(sum(col('fragile_only_pnl_per_ct')))}/ct (worst {fmt$(worst(col('fragile_only_pnl_per_ct')))}, {variantStats('fragile_only_pnl_per_ct')}).
                  </Typography>
                </>
              );
            })()}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
              Replay baseline (8/24–10/2, 24 trades): actual +22/ct, worst −38; always-on +34/ct, worst −28 (3 saves, 9 scratches). Per contract, replayed on 1-min bars.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {(!studyFilter.length || studyFilter.includes('ai_exit')) && (
        <Accordion defaultExpanded={counts.ai_exit > 0} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>H · AI exit desk ("should we exit now?") — {counts.ai_exit} scored trade(s), leader</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {counts.ai_exit === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No scored leader trades in range (rows start with v0.45.0).</Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'Credit', 'Exit', 'Actual P&L/ct', 'Votes', 'First confident exit', 'AI ≥ 0.70 (/ct)', 'AI any (/ct)', 'AI p≥0.50 (/ct)', 'AI ev (/ct)', 'p(breach) max', 'Brier', 'Giveback (/ct)', 'Δ vs actual', 'Latency', 'Model'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.ai_exit.map((e) => {
                      const d = e.eod || {};
                      const delta = d.ai_hi_minus_actual_per_ct;
                      const isOpen = aiExpanded === e.date;
                      return (
                        <React.Fragment key={e.date}>
                          <TableRow
                            hover
                            onClick={() => setAiExpanded(isOpen ? null : e.date)}
                            sx={{ opacity: e.eod ? 1 : 0.6, cursor: (e.queries?.length || 0) > 0 ? 'pointer' : 'default' }}
                          >
                            <TableCell>{e.date}{!e.eod && <Chip label="pending" size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}</TableCell>
                            <TableCell>${Number(d.credit ?? 0).toFixed(2)}</TableCell>
                            <TableCell><Chip label={(d.exit_reason || '?').replace(/_/g, ' ')} size="small" sx={{ fontSize: '0.6rem' }} /></TableCell>
                            <TableCell sx={{ fontWeight: 600, color: (d.actual_pnl_per_ct ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                              {fmt$(d.actual_pnl_per_ct)} <Chip label="REAL" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
                            </TableCell>
                            <TableCell>
                              {d.n_exit_votes ?? 0} exit / {d.n_hold_votes ?? 0} hold
                              {(d.n_errors ?? 0) > 0 && <span style={{ opacity: 0.55 }}> · {d.n_errors} err</span>}
                            </TableCell>
                            <TableCell>
                              {d.first_hi_exit_at ? (
                                <MuiTooltip title={d.first_hi_exit_reason || ''}>
                                  <span>{fmtTime(d.first_hi_exit_at)} @ ${Number(d.first_hi_exit_debit ?? 0).toFixed(2)} (conf {Number(d.first_hi_exit_conf ?? 0).toFixed(2)})</span>
                                </MuiTooltip>
                              ) : 'none'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{fmt$(d.ai_hi_pnl_per_ct)}</TableCell>
                            <TableCell>{fmt$(d.ai_any_pnl_per_ct)}</TableCell>
                            {/* v0.48.0 p-variant columns — '—' on pre-10/7 (v1) rows */}
                            <TableCell sx={{ fontWeight: 600 }}>{fmt$(d.ai_p_pnl_per_ct)}</TableCell>
                            {/* v0.52.0 (v3 prompt): exit at the first query where cutting
                                at the mark beats the model's own p-weighted hold */}
                            <TableCell sx={{ fontWeight: 600 }}>
                              {d.ai_ev_pnl_per_ct != null
                                ? <>{fmt$(d.ai_ev_pnl_per_ct)}{d.first_ev_exit_at ? ` @ ${fmtTime(d.first_ev_exit_at)}` : ''}</>
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {d.breach_prob_max != null
                                ? `${Number(d.breach_prob_max).toFixed(2)}${d.breach_prob_max_at ? ` @ ${fmtTime(d.breach_prob_max_at)}` : ''}`
                                : '—'}
                            </TableCell>
                            <TableCell>{d.brier != null ? Number(d.brier).toFixed(3) : '—'}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{fmt$(d.giveback_pnl_per_ct)}</TableCell>
                            <TableCell sx={{ color: (delta ?? 0) > 0 ? 'success.main' : 'text.secondary' }}>{fmt$(delta)}</TableCell>
                            <TableCell>{d.avg_latency_ms != null ? `${Math.round(d.avg_latency_ms)} ms` : '—'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{d.model ?? '—'}</TableCell>
                          </TableRow>
                          {isOpen && (e.queries?.length || 0) > 0 && (
                            <TableRow>
                              <TableCell colSpan={16} sx={{ py: 1, bgcolor: 'rgba(128,128,128,0.06)' }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>Verdict trail ({e.queries!.length} queries):</Typography>
                                {e.queries!.map((q, i) => {
                                  const thr = q.conf_threshold ?? 0.7;
                                  // v0.60.0 (prompt v5): a misread vote was discarded
                                  // (no live exit, not scored) and re-asked — violet
                                  const color = q.error ? 'transparent'
                                    : q.misread ? '#ba68c8'
                                    : q.verdict === 'exit' ? ((q.confidence ?? 0) >= thr ? '#ef5350' : '#ffb74d')
                                    : 'rgba(128,128,128,0.6)';
                                  return (
                                    <MuiTooltip key={i} title={`${(q.ts || '').slice(11, 16)} ${q.error ? `error: ${q.error}` : `${q.verdict ?? '—'}${q.confidence != null ? ` (${q.confidence.toFixed(2)})` : ''}${q.misread ? ` · MISREAD (${q.misread.replace(/_/g, ' ')}) — vote discarded, re-asked` : ''}${q.escalated ? ` · escalated to ${q.interval_min ?? 1}-min queries` : ''}${q.reason ? ` — ${q.reason}` : ''}`}`}>
                                      <span style={{
                                        display: 'inline-block', width: 9, height: 9, borderRadius: '50%', margin: '0 2px',
                                        backgroundColor: color, border: q.error ? '1.5px solid rgba(128,128,128,0.6)' : 'none', verticalAlign: 'middle',
                                      }} />
                                    </MuiTooltip>
                                  );
                                })}
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                  grey = hold · amber = exit below threshold · red = exit ≥ threshold · violet = misread (discarded) · hollow = error
                                </Typography>
                                {/* v0.48.0: breach_prob 0–1 line with the 0.50 threshold dashed */}
                                {e.queries!.some((q) => q.breach_prob != null) && (
                                  <Box sx={{ mt: 1, maxWidth: 560 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      p(breach before the resting close fills) — v2 continuous score:
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={90}>
                                      <LineChart
                                        data={e.queries!.filter((q) => q.breach_prob != null).map((q) => ({
                                          t: (q.ts || '').slice(11, 16), p: q.breach_prob,
                                        }))}
                                        margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
                                      >
                                        <XAxis dataKey="t" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                                        <YAxis domain={[0, 1]} ticks={[0, 0.5, 1]} tick={{ fontSize: 9 }} width={26} />
                                        <RechartsTooltip formatter={(v: any) => [Number(v).toFixed(2), 'p(breach)']} />
                                        <ReferenceLine y={0.5} stroke="#ffb74d" strokeDasharray="5 3" />
                                        <Line type="monotone" dataKey="p" stroke="#ef5350" dot={{ r: 2 }} strokeWidth={1.5} isAnimationActive={false} />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {(() => {
              const scored = visible.ai_exit.filter((e) => e.eod);
              if (!scored.length) return null;
              const col = (k: string) => scored.map((e) => (e.eod as any)[k]).filter((v: any) => v != null);
              const sum = (vs: number[]) => vs.reduce((s, v) => s + v, 0);
              const worst = (vs: number[]) => (vs.length ? Math.min(...vs) : null);
              const variantStats = (k: string) => {
                const saves = scored.filter((e) => (e.eod!.actual_pnl_per_ct ?? 0) < 0 && ((e.eod as any)[k] ?? 0) >= 0).length;
                const scratches = scored.filter((e) => (e.eod!.actual_pnl_per_ct ?? 0) > 0 && ((e.eod as any)[k] ?? 0) <= 0).length;
                return `${saves}S/${scratches}Sc`;
              };
              // v0.52.0: breach_prob means different things under the v2 and
              // v3+ prompts ("crosses the line before the close fills" vs
              // "ends on a loss exit"), so their Brier means never mix.
              // v2 rows carry no prompt_version; v3/v4 (v0.58+) share the
              // loss-exit definition and pool together.
              const brierMean = (pred: (e: StudyEntry) => boolean) => {
                const vs = scored.filter((e) => e.eod!.brier != null && pred(e)).map((e) => e.eod!.brier as number);
                return vs.length ? { n: vs.length, m: (vs.reduce((s, v) => s + v, 0) / vs.length).toFixed(3) } : null;
              };
              const b2 = brierMean((e) => e.eod!.prompt_version == null);
              const b3 = brierMean((e) => (e.eod!.prompt_version ?? 0) >= 3);
              return (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  n={scored.length} · Σ actual (REAL) {fmt$(sum(col('actual_pnl_per_ct')))}/ct (worst {fmt$(worst(col('actual_pnl_per_ct')))}) ·
                  Σ AI≥0.70 {fmt$(sum(col('ai_hi_pnl_per_ct')))}/ct (worst {fmt$(worst(col('ai_hi_pnl_per_ct')))}, {variantStats('ai_hi_pnl_per_ct')}) ·
                  Σ AI any {fmt$(sum(col('ai_any_pnl_per_ct')))}/ct (worst {fmt$(worst(col('ai_any_pnl_per_ct')))}, {variantStats('ai_any_pnl_per_ct')}) ·
                  Σ AI p≥0.50 {fmt$(sum(col('ai_p_pnl_per_ct')))}/ct (worst {fmt$(worst(col('ai_p_pnl_per_ct')))}, {variantStats('ai_p_pnl_per_ct')}) ·
                  Σ AI ev {fmt$(sum(col('ai_ev_pnl_per_ct')))}/ct (worst {fmt$(worst(col('ai_ev_pnl_per_ct')))}, {variantStats('ai_ev_pnl_per_ct')}) ·
                  Σ giveback {fmt$(sum(col('giveback_pnl_per_ct')))}/ct (worst {fmt$(worst(col('giveback_pnl_per_ct')))}, {variantStats('giveback_pnl_per_ct')})
                  {b2 && <> · mean Brier v2 {b2.m} (n={b2.n})</>}
                  {b3 && <> · mean Brier v3+ {b3.m} (n={b3.n})</>}
                  {(b2 || b3) && <> (0 = perfect, 0.25 = coin flip; v1 rows excluded; v2/v3 kept separate — breach_prob changed meaning)</>}. S = saves, Sc = scratches.
                </Typography>
              );
            })()}
            {(() => {
              // v0.60.0 (prompt v5): misreads per day — the model's error rate.
              // n_misreads comes from AI_EXIT_SHADOW_EOD; pre-v5 days carry no
              // field and are excluded (not zero — the check didn't run).
              const days = visible.ai_exit
                .filter((e) => e.eod && (e.eod as any).n_misreads != null)
                .map((e) => ({
                  date: e.date,
                  misreads: (e.eod as any).n_misreads as number,
                  queries: e.queries?.length ?? 0,
                }))
                .sort((a, b) => a.date.localeCompare(b.date));
              if (!days.length) return null;
              const totM = days.reduce((s, d) => s + d.misreads, 0);
              const totQ = days.reduce((s, d) => s + d.queries, 0);
              return (
                <Box sx={{ mt: 1, maxWidth: 560 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Misreads per day (v5 misread check, 10/20+): the model said the trade's direction backwards and the vote was discarded —
                    {' '}{totM} of {totQ} queries ({totQ ? ((totM / totQ) * 100).toFixed(1) : '0'}%) over {days.length} day(s).
                  </Typography>
                  <ResponsiveContainer width="100%" height={90}>
                    <BarChart data={days} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                      <YAxis allowDecimals={false} tick={{ fontSize: 9 }} width={22} />
                      <RechartsTooltip formatter={(v: any, _n: any, p: any) => [`${v} misread(s) / ${p?.payload?.queries ?? '?'} queries`, p?.payload?.date]} />
                      <Bar dataKey="misreads" fill="#ba68c8" isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              );
            })()}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
              Baseline: the live probe on the 10/2 11:03 snapshot said exit 0.85 (−7/ct vs −15 actual); a calm 55%-captured winner said hold 0.90.
              v2 probe (10/7): at 10:54 (mid $0.20, 0.08% from exit) it said exit 0.86 / breach_prob 0.72 — the trade recovered to +8/ct, so that call would have cost 16/ct; at 11:17 hold / 0.34.
              10/14 (the trade that motivated v3): v2 exit votes 11:41 / 11:46 / 11:56, actual −23/ct (operator cut), paper stop −31/ct. The score, not the story, decides. Per contract, replayed on 1-min bars.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {(!studyFilter.length || studyFilter.includes('sigma_put')) && (
        <Accordion defaultExpanded={counts.sigma_put > 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>
              I · σ put strike (Caleb's distance rule) — {counts.sigma_put} scored POSITIVE/MIXED morning(s), live window
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ToggleButtonGroup
              size="small" exclusive value={sigmaWindow}
              onChange={(_, v) => v && setSigmaWindow(v)} sx={{ mb: 1 }}
            >
              <ToggleButton value="live" sx={{ py: 0, px: 1.5, textTransform: 'none' }}>live (10:15 — our window)</ToggleButton>
              <ToggleButton value="early" sx={{ py: 0, px: 1.5, textTransform: 'none' }}>early (09:35 — read last)</ToggleButton>
            </ToggleButtonGroup>
            {(() => {
              const rows = visible.sigma_put.filter((e) => (e.window || 'live') === sigmaWindow);
              if (!rows.length) {
                return <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No rows in range (shadow goes live from the next engine restart after 10/12).</Typography>;
              }
              const realPerCt = (e: StudyEntry): number | null => {
                const c = e.real?.trade?.contracts; const p = e.real?.realized_pnl;
                return c && p != null ? p / c : null;
              };
              return (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['Date', 'Regime', 'IV', 'Shadow spread', 'σ dist', 'Credit', 'Recorded', 'Put wall', 'Short touched', 'Worst mark', 'Live 4× (/ct)', 'Confirmed + 8× (/ct)', 'Hold (/ct)', 'Our real trade'].map((h) => (
                            <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((e) => {
                          const d = e.eod || {}; const r = e.record || {};
                          const reg = String(d.regime || r.regime || '').toLowerCase();
                          const isNeg = reg === 'negative' || Object.keys(d.blocks || {}).some((b) => b.startsWith('regime_negative'));
                          const rules = d.rules || {};
                          const cell = (rule: any) => (rule?.exit ? `${String(rule.exit).replace(/_/g, ' ')} @ ${fmtTime(rule.at)} ${fmt$(rule.pnl_per_ct)}` : '—');
                          if (d.entered === false) {
                            const blocks = Object.entries(d.blocks || {}).sort((a: any, b: any) => b[1] - a[1]).slice(0, 3)
                              .map(([k, v]) => `${k}×${v}`).join(', ');
                            return (
                              <TableRow key={`${e.date}|${e.window}`} sx={{ opacity: 0.55 }}>
                                <TableCell>{e.date}</TableCell>
                                <TableCell>{reg || '—'}</TableCell>
                                <TableCell colSpan={11}>
                                  no entry — {d.best_in_scope_credit != null ? `best $${Number(d.best_in_scope_credit).toFixed(2)}${d.best_in_scope_at ? ` at ${fmtTime(d.best_in_scope_at)}` : ''} vs floor $${Number(d.floor ?? 0.08).toFixed(2)}` : 'no qualifying scan'}
                                  {blocks ? ` · blocks: ${blocks}` : ''}
                                </TableCell>
                                <TableCell><RealCell real={e.real} /></TableCell>
                              </TableRow>
                            );
                          }
                          return (
                            <TableRow key={`${e.date}|${e.window}`} sx={{ opacity: e.eod ? (isNeg ? 0.5 : 1) : 0.6 }}>
                              <TableCell>{e.date}{!e.eod && <Chip label="pending" size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}{isNeg && <Chip label="NEG — not counted" size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}</TableCell>
                              <TableCell>{reg || '—'}</TableCell>
                              <TableCell>{(d.atm_iv ?? r.atm_iv) != null ? `${((d.atm_iv ?? r.atm_iv) * 100).toFixed(1)}%` : '—'}</TableCell>
                              <TableCell>{d.short_strike ?? r.short_strike}/{d.long_strike ?? r.long_strike}p</TableCell>
                              <TableCell>{(d.sigma_dist ?? r.sigma_dist) != null ? `${Number(d.sigma_dist ?? r.sigma_dist).toFixed(2)}σ` : '—'}</TableCell>
                              <TableCell>${Number(d.credit ?? r.credit ?? 0).toFixed(2)}</TableCell>
                              <TableCell>{fmtTime(d.recorded_at || r.ts_et)}</TableCell>
                              <TableCell>{d.put_wall ?? r.put_wall ?? '—'}</TableCell>
                              <TableCell>{d.short_touched ? <span style={{ color: '#ef5350' }}>●</span> : 'no'}</TableCell>
                              <TableCell>{d.max_mark_mult != null ? `${Number(d.max_mark_mult).toFixed(1)}×` : '—'}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{cell(rules.live_stop)}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{cell(rules.confirm_backstop)}</TableCell>
                              <TableCell>{fmt$(rules.hold?.pnl_per_ct)}</TableCell>
                              <TableCell><RealCell real={e.real} /></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {(() => {
                    // Footer over scored, entered, POSITIVE/MIXED rows in this window
                    const scored = rows.filter((e) => e.eod && e.eod.entered !== false
                      && String(e.eod.regime || '').toLowerCase() !== 'negative');
                    if (!scored.length) return null;
                    const ruleVals = (name: string) => scored.map((e) => e.eod!.rules?.[name]?.pnl_per_ct).filter((v: any) => v != null);
                    const sum = (vs: number[]) => vs.reduce((s, v) => s + v, 0);
                    const stat = (name: string) => {
                      const vs = ruleVals(name);
                      const w = vs.filter((v) => v > 0).length;
                      return `${w}W/${vs.length - w}L Σ ${fmt$(sum(vs))}/ct worst ${fmt$(vs.length ? Math.min(...vs) : null)}`;
                    };
                    const realVals = scored.map((e) => realPerCt(e)).filter((v): v is number => v != null);
                    const shadowOnly = scored.filter((e) => !e.real?.trade).length;
                    return (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                        {sigmaWindow} window, POSITIVE/MIXED: n={scored.length} · live 4×: {stat('live_stop')} · confirmed+8×: {stat('confirm_backstop')} · hold: {stat('hold')} ·
                        Σ our real P&L on the same mornings {fmt$(sum(realVals))}/ct (REAL) · shadow entered / we did not: {shadowOnly}.
                      </Typography>
                    );
                  })()}
                </>
              );
            })()}
          </AccordionDetails>
        </Accordion>
      )}

      {(!studyFilter.length || studyFilter.includes('hold_shadow')) && (
        <Accordion defaultExpanded={counts.hold_shadow > 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>
              J · In-trade shadows (bleed exit · early mixed) — {counts.hold_shadow} scored trade(s), leader
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {visible.hold_shadow.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No rows in range (shadow goes live from the next engine restart after 10/14).</Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'Direction / strikes', 'Credit', 'Exit', 'Actual P&L/ct', 'Bleed (/ct)', 'Early mixed', 'Δ bleed'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.hold_shadow.map((e) => {
                      const d = e.eod || {};
                      const bf = e.bleed_fire || {}; const em = e.early_mixed_fire || {};
                      const key = String(d.opened_at || bf.opened_at || em.opened_at || e.date);
                      const dir = bf.direction || em.direction || e.real?.trade?.direction;
                      const strikes = bf.short_strike != null ? `${bf.short_strike}/${bf.long_strike}`
                        : em.strikes ? String(em.strikes)
                        : e.real?.trade?.short_strike != null ? `${e.real.trade.short_strike}/${e.real.trade.long_strike}` : '—';
                      const credit = bf.entry_credit_per_share ?? em.credit ?? e.real?.trade?.entry_credit_per_share;
                      return (
                        <React.Fragment key={key}>
                          <TableRow sx={{ opacity: e.eod ? 1 : 0.6 }}>
                            <TableCell>{e.date}{!e.eod && <Chip label="pending" size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}</TableCell>
                            <TableCell>{dir ? `${dir} ${strikes}` : strikes}</TableCell>
                            <TableCell>{credit != null ? `$${Number(credit).toFixed(2)}` : '—'}</TableCell>
                            <TableCell><Chip label={(d.exit_reason || '?').replace(/_/g, ' ')} size="small" sx={{ fontSize: '0.6rem' }} /></TableCell>
                            <TableCell sx={{ fontWeight: 600, color: (d.actual_pnl_per_ct ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                              {fmt$(d.actual_pnl_per_ct)} <Chip label="REAL" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {d.bleed_fired_at ? `${fmt$(d.bleed_pnl_per_ct)} @ ${fmtTime(d.bleed_fired_at)}` : 'never fired'}
                            </TableCell>
                            <TableCell>
                              {d.early_mixed
                                ? <Chip label="MIXED pre-09:46" size="small" color="warning" sx={{ fontSize: '0.6rem' }} />
                                : '—'}
                            </TableCell>
                            <TableCell sx={{ color: (d.bleed_minus_actual_per_ct ?? 0) > 0 ? 'success.main' : 'text.secondary' }}>{fmt$(d.bleed_minus_actual_per_ct)}</TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {(() => {
              const scored = visible.hold_shadow.filter((e) => e.eod);
              if (!scored.length) return null;
              // "never fired" counts as P&L = actual (per the row contract)
              const variant = (pnlKey: string, firedKey: string) => {
                const vs = scored.map((e) => (e.eod![pnlKey] ?? e.eod!.actual_pnl_per_ct)).filter((v: any) => v != null);
                const scratched = scored.filter((e) => (e.eod!.actual_pnl_per_ct ?? 0) > 0
                  && e.eod![firedKey] && (e.eod![pnlKey] ?? 0) <= 0).length;
                return { sum: vs.reduce((s: number, v: number) => s + v, 0), worst: vs.length ? Math.min(...vs) : null, scratched };
              };
              const actual = scored.map((e) => e.eod!.actual_pnl_per_ct).filter((v: any) => v != null);
              const bleed = variant('bleed_pnl_per_ct', 'bleed_fired_at');
              const tagged = scored.filter((e) => e.eod!.early_mixed);
              const tagWins = tagged.filter((e) => (e.eod!.actual_pnl_per_ct ?? 0) > 0).length;
              const tagSum = tagged.reduce((s, e) => s + (e.eod!.actual_pnl_per_ct || 0), 0);
              return (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  n={scored.length} · Σ actual (REAL) {fmt$(actual.reduce((s: number, v: number) => s + v, 0))}/ct (worst {fmt$(actual.length ? Math.min(...actual) : null)}) ·
                  Σ bleed {fmt$(bleed.sum)}/ct (worst {fmt$(bleed.worst)}, scratched {bleed.scratched} winner{bleed.scratched === 1 ? '' : 's'}) ·
                  early-mixed: {tagged.length} tagged ({tagWins}W/{tagged.length - tagWins}L), Σ actual on tagged {fmt$(tagSum)}/ct (= what a gate would have avoided).
                </Typography>
              );
            })()}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
              Arm nothing before ~10 fires per variant; a variant is a candidate only if Σ beats actual AND it scratched no more than one winner.
              Baseline from the 10/14 replay (32 trades): bleed 2.5×/0.15%/10m +22/ct (9/23 −29 → −17, 10/14 −36 → −26), touched no winner; early mixed 6 trades, 3 losses, −76/ct.
              Per contract, replayed on 1-min bars; a shadow exit that fired after the real close counts as "never fired" (P&amp;L = actual).
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {(!studyFilter.length || studyFilter.includes('entry_risk')) && (
        <Accordion defaultExpanded={counts.entry_risk > 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>
              K · Entry-risk — high-IV ×0.60 cap (LIVE) · session extreme — {counts.entry_risk} leader trade(s)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {(() => {
              const filled = visible.entry_risk.filter((e) => e.open);
              if (!filled.length) {
                return <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No rows in range.</Typography>;
              }
              const pnlPerCt = (e: StudyEntry): number | null => {
                const t = e.close?.realized_pnl_total; const n = e.open?.contracts;
                return t != null && n ? t / n : null;
              };
              return (
                <>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['Date', 'Strikes', 'Credit', 'ATM IV', 'Actual P&L/ct', 'High-IV ×0.60 cap (R18b, LIVE)', 'Session extreme'].map((h) => (
                            <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filled.map((e) => {
                          const o = e.open!; const g = o.gex_at_entry || {};
                          const se = e.session_extreme || g.session_extreme;
                          const pct = pnlPerCt(e);
                          // R18b went LIVE v0.54.0: capped entries carry
                          // high_iv_capped_from (the cap IS the actual size);
                          // pre-LIVE rows carry the would-cap pair instead
                          const capLive = g.high_iv_capped_from;
                          const capFrom = g.high_iv_would_cap_from; const capTo = g.high_iv_would_cap_to;
                          const pnlAtCap = (pct != null && capFrom && capTo != null && o.contracts)
                            ? pct * o.contracts * (capTo / capFrom) : null;
                          const margin = se?.margin;
                          return (
                            <TableRow key={`${e.account}|${o.opened_at || o.ts_et}`} sx={{ opacity: e.close ? 1 : 0.6 }}>
                              <TableCell>{e.date}{!e.close && <Chip label="open" size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}</TableCell>
                              <TableCell>{o.direction} {o.short_strike}/{o.long_strike}</TableCell>
                              <TableCell>${Number(o.entry_credit_per_share ?? 0).toFixed(2)}</TableCell>
                              <TableCell>{(g.atm_iv ?? g.iv_margin_shadow?.atm_iv) != null ? `${(Number(g.atm_iv ?? g.iv_margin_shadow?.atm_iv) * 100).toFixed(1)}%` : '—'}</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: (pct ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                                {fmt$(pct)} {pct != null && <Chip label="REAL" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />}
                              </TableCell>
                              <TableCell>
                                {capLive != null
                                  ? <>×{Number(capLive).toFixed(2)} → ×0.60 <Chip label="LIVE" size="small" color="success" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} /></>
                                  : capTo != null && capFrom != null
                                  ? <>×{Number(capFrom).toFixed(2)} → ×{Number(capTo).toFixed(2)}{pnlAtCap != null ? ` · $${pnlAtCap.toFixed(0)} at cap` : ''}</>
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                {margin != null
                                  ? <>
                                      {Number(margin).toFixed(2)} pts{se?.source ? ` (${se.source})` : ''}
                                      {(se?.traded_through || margin <= 0) && <Chip label="traded through" size="small" color="error" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}
                                    </>
                                  : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {(() => {
                    const sized = filled.filter((e) => e.close && e.open!.gex_at_entry?.high_iv_would_cap_to != null && e.open!.gex_at_entry?.high_iv_would_cap_from);
                    if (!sized.length) return null;
                    const sumActual = sized.reduce((s, e) => s + ((pnlPerCt(e) ?? 0) * (e.open!.contracts || 0)), 0);
                    const sumCapped = sized.reduce((s, e) => {
                      const g = e.open!.gex_at_entry;
                      return s + ((pnlPerCt(e) ?? 0) * (e.open!.contracts || 0) * (g.high_iv_would_cap_to / g.high_iv_would_cap_from));
                    }, 0);
                    return (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                        {sized.length > 0 && <>R18 high-IV cap (n={sized.length}): Σ ${sumActual.toFixed(0)} at actual size vs Σ ${sumCapped.toFixed(0)} at the ×0.60 cap.</>}
                      </Typography>
                    );
                  })()}
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                    R17 (IV margin) and R18a (IV-aware sizing) closed 10/31 — see the Closed list under the Roadmap; their shadow rows are ignored here.
                    The ×0.60 high-IV cap (R18b) is LIVE since v0.54.0; the session-extreme margin is a tag, never a gate.
                  </Typography>
                </>
              );
            })()}
          </AccordionDetails>
        </Accordion>
      )}

      {(!studyFilter.length || studyFilter.includes('caleb_put')) && (
        <Accordion defaultExpanded={counts.caleb_put > 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>
              L · Caleb-distance put (0.65% below spot) — {counts.caleb_put} scored morning(s)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {visible.caleb_put.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No rows in range (shadow goes live from the next engine restart after 10/16).</Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'Regime', 'ATM IV', '0.65% strike', 'First credit (mid / natural) vs $0.08', 'Floor window', 'Caleb exit (/ct)', 'Live 4× (/ct)', 'Our real trade', 'Short touched', 'Worst mark'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.caleb_put.map((e) => {
                      const d = e.eod_first || {}; const en = e.entry_first || {};
                      const fl = e.eod_floor || {}; const fen = e.entry_floor || {};
                      const notEntered = d.entered === false;
                      const strike = en.short_strike ?? d.best?.short_strike ?? e.scan?.ladder?.find((l) => l.dist === 0.0065)?.short_strike;
                      const long = en.long_strike;
                      const clears = d.clears_floor === true;
                      const rules = d.rules || {};
                      const isOpen = lExpanded === e.date;
                      const hasLadder = (e.scan?.ladder?.length ?? 0) > 0;
                      return (
                        <React.Fragment key={e.date}>
                          <TableRow
                            hover
                            onClick={() => setLExpanded(isOpen ? null : e.date)}
                            sx={{ opacity: e.eod_first ? (notEntered ? 0.6 : 1) : 0.6, cursor: hasLadder ? 'pointer' : 'default' }}
                          >
                            <TableCell>{e.date}{!e.eod_first && <Chip label="pending" size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}</TableCell>
                            <TableCell>{d.regime ?? en.regime ?? '—'}</TableCell>
                            <TableCell>{(d.atm_iv ?? en.atm_iv) != null ? `${((d.atm_iv ?? en.atm_iv) * 100).toFixed(1)}%` : '—'}</TableCell>
                            <TableCell>{strike != null ? `${strike}${long != null ? `/${long}` : ''}p` : '—'}</TableCell>
                            <TableCell sx={{ color: clears ? 'success.main' : 'text.secondary', fontWeight: clears ? 600 : 400 }}>
                              {notEntered && d.best
                                ? `never cleared — best $${Number(d.best.credit ?? 0).toFixed(2)}${d.best.at ? ` at ${fmtTime(d.best.at)}` : ''} over ${d.scans ?? '?'} scans`
                                : (d.credit ?? en.credit) != null
                                ? `$${Number(d.credit ?? en.credit).toFixed(2)} / $${Number(d.credit_natural ?? en.credit_natural ?? 0).toFixed(2)}${clears ? ' ≥ floor' : ' < floor'}`
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {e.eod_floor || e.entry_floor
                                ? `${fen.ts_et ? fmtTime(fen.ts_et) : '?'} @ $${Number(fl.credit ?? fen.credit ?? 0).toFixed(2)}`
                                : clears ? 'same as first' : 'never'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {rules.caleb ? `${String(rules.caleb.exit ?? '?').replace(/_/g, ' ')}${rules.caleb.at ? ` @ ${fmtTime(rules.caleb.at)}` : ''} ${fmt$(rules.caleb.pnl_per_ct)}` : '—'}
                            </TableCell>
                            <TableCell>{rules.live_4x ? fmt$(rules.live_4x.pnl_per_ct) : '—'}</TableCell>
                            <TableCell><RealCell real={e.real} /></TableCell>
                            <TableCell>{d.short_touched ? <span style={{ color: '#ef5350' }}>●</span> : notEntered ? '—' : 'no'}</TableCell>
                            <TableCell>{d.max_mark_mult != null ? `${Number(d.max_mark_mult).toFixed(1)}×` : '—'}</TableCell>
                          </TableRow>
                          {isOpen && hasLadder && (
                            <TableRow>
                              <TableCell colSpan={11} sx={{ py: 1, bgcolor: 'rgba(128,128,128,0.06)' }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
                                  09:35 ladder (spot {e.scan!.spot ?? '?'}):
                                </Typography>
                                {e.scan!.ladder!.map((l, i) => (
                                  <Chip
                                    key={i}
                                    label={`${l.dist != null ? (l.dist * 100).toFixed(2) : '?'}% ${l.short_strike ?? '?'} $${Number(l.credit ?? 0).toFixed(2)}${l.credit_natural != null ? `/$${Number(l.credit_natural).toFixed(2)}` : ''}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem', height: 18, mr: 0.4, opacity: l.quotes_ok === false ? 0.45 : 1 }}
                                  />
                                ))}
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                  distance % · short strike · credit mid/natural (greyed = quotes not OK)
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {(() => {
              const scored = visible.caleb_put.filter((e) => e.eod_first);
              if (!scored.length) return null;
              const cleared = scored.filter((e) => e.eod_first!.clears_floor === true).length;
              const entered = scored.filter((e) => e.eod_first!.entered !== false && e.eod_first!.rules?.caleb);
              const calebVals = entered.map((e) => e.eod_first!.rules.caleb.pnl_per_ct).filter((v: any) => v != null);
              const losers = calebVals.filter((v: number) => v < 0).length;
              const realVals = entered.map((e) => {
                const c = e.real?.trade?.contracts; const p = e.real?.realized_pnl;
                return c && p != null ? p / c : null;
              }).filter((v): v is number => v != null);
              const sum = (vs: number[]) => vs.reduce((s, v) => s + v, 0);
              return (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  0.65% mid cleared $0.08 on {cleared} of {scored.length} morning(s) ·
                  Σ caleb {fmt$(sum(calebVals))}/ct ({losers} loser{losers === 1 ? '' : 's'}) vs Σ our real trades {fmt$(sum(realVals))}/ct (REAL) on the same mornings.
                </Typography>
              );
            })()}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
              ~10 mornings; arm only if the live credit is there (most first mids ≥ $0.08) AND Σ caleb beats our real trades with no worse worst day.
              Replay baseline (bar closes, 8/24–10/16): 0.65% @09:35 + Caleb exits +189/ct, 0 losers; our strikes + Caleb exits −8/ct. Per contract, replayed on 1-min bars; never in the P&amp;L.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {(!studyFilter.length || studyFilter.includes('profit_floor_ai')) && (() => {
        const VARIANTS = ['ai_veto', 'bp_lt_0.10', 'bp_lt_0.15', 'bp_lt_0.20'];
        const variantKeys = Array.from(new Set([
          ...VARIANTS,
          ...visible.profit_floor_ai.flatMap((e) => Object.keys(e.result?.variants || {})),
        ]));
        const $2 = (v: any) => (v != null ? `$${Number(v).toFixed(2)}` : '—');
        const exitLabel = (x: any) => (x ? String(x).replace(/_/g, ' ') : '—');
        return (
          <Accordion defaultExpanded={counts.profit_floor_ai > 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 'bold' }}>
                O · AI on the profit floor (floor vs hold vs AI-gated variants) — {counts.profit_floor_ai} scored fire(s)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {visible.profit_floor_ai.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No rows in range (shadow live from v0.67.0, 10/26; a row appears only when a leader profit_floor_close fires).
                </Typography>
              ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Date', 'Grade', 'Peak captured', 'Fire time', 'AI vote', 'Floor P&L/ct', 'Held P&L/ct', ...variantKeys].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visible.profit_floor_ai.map((e) => {
                        const s = e.start || {}; const r = e.result;
                        const ai = s.ai ?? r?.ai;
                        const bp = ai?.breach_prob;
                        const key = `${e.account}|${(s.opened_at || r?.opened_at || '').slice(0, 19)}`;
                        const isOpen = oExpanded === key;
                        const hasPath = (e.path?.length ?? 0) > 0;
                        const peak = s.peak_captured;
                        return (
                          <React.Fragment key={key}>
                            <TableRow
                              hover
                              onClick={() => hasPath && setOExpanded(isOpen ? null : key)}
                              sx={{ opacity: r ? 1 : 0.6, cursor: hasPath ? 'pointer' : 'default' }}
                            >
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.date}{e.account ? <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>{e.account}</Typography> : null}</TableCell>
                              <TableCell>{s.entry_grade ?? r?.entry_grade ?? '—'}</TableCell>
                              <TableCell>{peak != null ? `${(Number(peak) * 100).toFixed(0)}%` : '—'}</TableCell>
                              <TableCell>{fmtTime(s.fired_at || r?.fired_at)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {ai ? (
                                  <MuiTooltip title={ai.reason || ''}>
                                    <Box component="span">
                                      <Chip label={`${ai.verdict ?? '?'}${ai.confidence != null ? ` ${Number(ai.confidence).toFixed(2)}` : ''}`} size="small"
                                        color={String(ai.verdict).toUpperCase() === 'EXIT' ? 'error' : 'default'} variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                                      {bp != null && (
                                        <Chip label={`bp ${Number(bp).toFixed(2)}`} size="small" color={Number(bp) >= 0.2 ? 'error' : 'default'}
                                          variant={Number(bp) >= 0.2 ? 'filled' : 'outlined'} sx={{ ml: 0.5, fontSize: '0.6rem', height: 18 }} />
                                      )}
                                    </Box>
                                  </MuiTooltip>
                                ) : <Typography variant="caption" color="text.secondary">no vote</Typography>}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {fmt$(r?.floor_pnl_per_ct)} <Chip label="REAL" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
                                {r?.floor_debit != null && (
                                  <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>paid {$2(r.floor_debit)}</Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {fmt$(r?.held_pnl_per_ct)}
                                {r?.held && (
                                  <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                    {exitLabel(r.held.exit)}{r.held.at ? ` ${String(r.held.at).length > 5 ? fmtTime(r.held.at) : r.held.at}` : ''}{r.held.debit != null ? ` @ ${$2(r.held.debit)}` : ''}
                                  </Typography>
                                )}
                              </TableCell>
                              {variantKeys.map((k) => {
                                const v = r?.variants?.[k];
                                const vs = v?.vs_floor_per_ct;
                                return (
                                  <TableCell key={k} sx={{ whiteSpace: 'nowrap', color: vs == null ? undefined : vs > 0 ? 'success.main' : vs < 0 ? 'error.main' : 'text.secondary' }}>
                                    {v ? <>{fmt$(v.pnl_per_ct)} <Typography component="span" variant="caption">{v.holds ? 'held' : 'floor'}{vs ? ` (${fmt$(vs)})` : ''}</Typography></> : '—'}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                            {isOpen && hasPath && (
                              <TableRow>
                                <TableCell colSpan={7 + variantKeys.length} sx={{ py: 1, bgcolor: 'rgba(128,128,128,0.06)' }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Spread mid after the floor fire (after-exit tracker, 1/min) — orange = floor {$2(e.lines?.floor)}, green = target {$2(e.lines?.target)}, red = stop {$2(e.lines?.stop)}
                                  </Typography>
                                  <Box sx={{ height: 160 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                      <LineChart data={e.path} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                                        <XAxis dataKey="t" tick={{ fontSize: 10 }} />
                                        <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10 }} width={44} tickFormatter={(v) => `$${Number(v).toFixed(2)}`} />
                                        <RechartsTooltip formatter={(v: any) => `$${Number(v).toFixed(2)}`} />
                                        {e.lines?.floor != null && <ReferenceLine y={Number(e.lines.floor)} stroke="#ffb74d" strokeDasharray="2 2" />}
                                        {e.lines?.target != null && <ReferenceLine y={Number(e.lines.target)} stroke="#66bb6a" strokeDasharray="4 3" />}
                                        {e.lines?.stop != null && <ReferenceLine y={Number(e.lines.stop)} stroke="#ef5350" strokeDasharray="4 3" />}
                                        <Line type="stepAfter" dataKey="mid" stroke="#90caf9" dot={false} strokeWidth={2} isAnimationActive={false} />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {(() => {
                const scored = visible.profit_floor_ai.filter((e) => e.result?.variants);
                if (!scored.length) return null;
                return (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    n {scored.length}
                    {variantKeys.map((k) => {
                      const vals = scored.map((e) => e.result!.variants[k]).filter(Boolean);
                      if (!vals.length) return null;
                      const sum = vals.reduce((a: number, v: any) => a + Number(v.vs_floor_per_ct ?? 0), 0);
                      const worst = Math.min(...vals.map((v: any) => Number(v.vs_floor_per_ct ?? 0)));
                      return <React.Fragment key={k}> · {k} Σ vs floor <b style={{ color: sum > 0 ? '#66bb6a' : sum < 0 ? '#ef5350' : undefined }}>{fmt$(sum)}</b>/ct, worst {fmt$(worst)}</React.Fragment>;
                    })}
                    {' '}· {scored.length < 10 ? `accumulating (${scored.length}/10)` : 'n ≥ 10 — ready to read'}
                  </Typography>
                );
              })()}
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                ~10 floor fires with a vote; a variant goes live only if its Σ beats the floor AND it never holds into a stop — operator&apos;s word.
                Replay baseline (analysis/profit_floor_replay.py, 13 B/C fires 8/24–10/26): floor +107/ct vs holding; the 4 fires with an AI vote all HOLD → ai_veto ≈ −37/ct (10/16 −51).
                Floor P&amp;L is the real fill; held and variant columns are replays. Never in the P&amp;L.
              </Typography>
            </AccordionDetails>
          </Accordion>
        );
      })()}

      {(!studyFilter.length || studyFilter.includes('caleb_call')) && (
        <Accordion defaultExpanded={counts.caleb_call > 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>
              N · Caleb-distance call (0.65% above spot) — {counts.caleb_call} scored morning(s)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {visible.caleb_call.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No rows in range (shadow goes live from the next engine restart after 10/26).</Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'Regime', 'ATM IV', 'Call wall', '0.65% strike', 'First credit (mid / natural) vs $0.08', 'Floor window', 'Caleb exit (/ct)', 'Live 4× (/ct)', 'Our real trade', "Caleb's real call", 'Short touched', 'Worst mark'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.caleb_call.map((e) => {
                      const d = e.eod_first || {}; const en = e.entry_first || {};
                      const fl = e.eod_floor || {}; const fen = e.entry_floor || {};
                      const notEntered = d.entered === false;
                      const strike = en.short_strike ?? d.best?.short_strike ?? e.scan?.ladder?.find((l) => l.dist === 0.0065)?.short_strike;
                      const long = en.long_strike;
                      const clears = d.clears_floor === true;
                      const rules = d.rules || {};
                      const cr = e.caleb_real;
                      const isOpen = nExpanded === e.date;
                      const hasLadder = (e.scan?.ladder?.length ?? 0) > 0;
                      return (
                        <React.Fragment key={e.date}>
                          <TableRow
                            hover
                            onClick={() => setNExpanded(isOpen ? null : e.date)}
                            sx={{ opacity: e.eod_first ? (notEntered ? 0.6 : 1) : 0.6, cursor: hasLadder ? 'pointer' : 'default' }}
                          >
                            <TableCell>{e.date}{!e.eod_first && <Chip label="pending" size="small" sx={{ ml: 0.5, fontSize: '0.55rem', height: 14 }} />}</TableCell>
                            <TableCell>{d.regime ?? en.regime ?? '—'}</TableCell>
                            <TableCell>{(d.atm_iv ?? en.atm_iv) != null ? `${((d.atm_iv ?? en.atm_iv) * 100).toFixed(1)}%` : '—'}</TableCell>
                            <TableCell>{d.call_wall ?? en.call_wall ?? e.scan?.call_wall ?? '—'}</TableCell>
                            <TableCell>{strike != null ? `${strike}${long != null ? `/${long}` : ''}c` : '—'}</TableCell>
                            <TableCell sx={{ color: clears ? 'success.main' : 'text.secondary', fontWeight: clears ? 600 : 400 }}>
                              {notEntered && d.best
                                ? `never cleared — best $${Number(d.best.credit ?? 0).toFixed(2)}${d.best.at ? ` at ${fmtTime(d.best.at)}` : ''} over ${d.scans ?? '?'} scans`
                                : (d.credit ?? en.credit) != null
                                ? `$${Number(d.credit ?? en.credit).toFixed(2)} / $${Number(d.credit_natural ?? en.credit_natural ?? 0).toFixed(2)}${clears ? ' ≥ floor' : ' < floor'}`
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {e.eod_floor || e.entry_floor
                                ? `${fen.ts_et ? fmtTime(fen.ts_et) : '?'} @ $${Number(fl.credit ?? fen.credit ?? 0).toFixed(2)}`
                                : clears ? 'same as first' : 'never'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {rules.caleb ? `${String(rules.caleb.exit ?? '?').replace(/_/g, ' ')}${rules.caleb.at ? ` @ ${rules.caleb.at}` : ''} ${fmt$(rules.caleb.pnl_per_ct)}` : '—'}
                            </TableCell>
                            <TableCell>{rules.live_4x ? fmt$(rules.live_4x.pnl_per_ct) : '—'}</TableCell>
                            <TableCell><RealCell real={e.real} /></TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {cr ? (
                                <>
                                  {cr.short_strike}/{cr.long_strike}c{cr.entry ? ` ${cr.entry}` : ''} @ {cr.credit != null ? `$${cr.credit.toFixed(2)}` : '?'}{' '}
                                  <span style={{ fontWeight: 600 }}>{cr.status === 'open' ? 'open' : `${fmt$(cr.pnl_per_ct)}${cr.approx ? ' ≈' : ''}`}</span>
                                </>
                              ) : <span style={{ opacity: 0.6 }}>—</span>}
                            </TableCell>
                            <TableCell>{d.short_touched ? <span style={{ color: '#ef5350' }}>●</span> : notEntered ? '—' : 'no'}</TableCell>
                            <TableCell>{d.max_mark_mult != null ? `${Number(d.max_mark_mult).toFixed(1)}×` : '—'}</TableCell>
                          </TableRow>
                          {isOpen && hasLadder && (
                            <TableRow>
                              <TableCell colSpan={13} sx={{ py: 1, bgcolor: 'rgba(128,128,128,0.06)' }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
                                  09:35 ladder (spot {e.scan!.spot ?? '?'}{e.scan!.call_wall != null ? `, call wall ${e.scan!.call_wall}` : ''}):
                                </Typography>
                                {e.scan!.ladder!.map((l, i) => (
                                  <Chip
                                    key={i}
                                    label={`+${l.dist != null ? (l.dist * 100).toFixed(2) : '?'}% ${l.short_strike ?? '?'} $${Number(l.credit ?? 0).toFixed(2)}${l.credit_natural != null ? `/$${Number(l.credit_natural).toFixed(2)}` : ''}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem', height: 18, mr: 0.4, opacity: l.quotes_ok === false ? 0.45 : 1 }}
                                  />
                                ))}
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                  distance above spot % · short strike · credit mid/natural (greyed = quotes not OK)
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {(() => {
              const scored = visible.caleb_call.filter((e) => e.eod_first);
              if (!scored.length) return null;
              const cleared = scored.filter((e) => e.eod_first!.clears_floor === true).length;
              const entered = scored.filter((e) => e.eod_first!.entered !== false && e.eod_first!.rules?.caleb);
              const sum = (vs: number[]) => vs.reduce((s, v) => s + v, 0);
              const pick = (es: StudyEntry[], k: 'caleb' | 'live_4x') =>
                es.map((e) => e.eod_first!.rules?.[k]?.pnl_per_ct).filter((v: any): v is number => v != null);
              const realPerCt = (e: StudyEntry) => {
                const c = e.real?.trade?.contracts; const p = e.real?.realized_pnl;
                return c && p != null ? p / c : null;
              };
              const calebVals = pick(entered, 'caleb');
              const realVals = entered.map(realPerCt).filter((v): v is number => v != null);
              const losers = calebVals.filter((v) => v < 0).length;
              const worst = calebVals.length ? Math.min(...calebVals) : null;
              const satOut = entered.filter((e) => !e.real?.trade);
              return (
                <>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    0.65% mid cleared $0.08 on {cleared} of {scored.length} morning(s) ·
                    Σ caleb {fmt$(sum(calebVals))}/ct ({losers} loser{losers === 1 ? '' : 's'}, worst {fmt$(worst)}) · Σ 4× {fmt$(sum(pick(entered, 'live_4x')))}/ct
                    {' '}vs Σ our real trades {fmt$(sum(realVals))}/ct (REAL) on the same mornings.
                  </Typography>
                  {satOut.length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      Days we sat out ({satOut.length}): Σ caleb {fmt$(sum(pick(satOut, 'caleb')))}/ct · Σ 4× {fmt$(sum(pick(satOut, 'live_4x')))}/ct.
                    </Typography>
                  )}
                </>
              );
            })()}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
              ~10 floor-clearing mornings; arm (a live call template) only if Σ beats our real trades with no worse worst day — operator&apos;s word.
              Replay baseline (8/24–10/23, 1-min closes): 12 of 44 mornings ≥ $0.08 at 09:35, his exits +31/ct (1 loser, −64 on 10/15), our 4× +66/ct; +55/ct on the 5 of those days we sat out.
              Caleb&apos;s real call trades come from the strategy&apos;s hand-kept OPG trade log (≈ = inferred values). Never in the P&amp;L.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {(!studyFilter.length || studyFilter.includes('patient_close')) && (
        <Accordion defaultExpanded={counts.patient_close > 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>
              M · Patient profit close (rest at the target vs the chase) — {counts.patient_close} scored close(s)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {visible.patient_close.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No rows in range (shadow live from v0.66.0, 10/26; a row appears only when a leader profit close fills above its resting limit).
              </Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'Fill time', 'Credit', 'Target', 'Paid', 'Outcome', 'Would pay', 'Waited', 'Worst mid', 'Saved /ct', 'Saved $ (leader)'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.patient_close.map((e) => {
                      const s = e.start || {}; const r = e.result;
                      const key = `${e.account}|${(s.opened_at || r?.opened_at || '').slice(0, 19)}`;
                      const todayEt = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
                      const outcome: string = r?.outcome ?? (e.date < todayEt ? 'unresolved' : 'pending');
                      const outcomeColor = outcome === 'filled' ? 'success' : outcome === 'timeout_chase' ? 'warning'
                        : (outcome === 'guard_chase' || outcome === 'forced_chase') ? 'error' : 'default';
                      const target = s.target ?? r?.target;
                      const paid = s.actual_debit ?? r?.actual_debit;
                      const saved = r?.saved_per_ct;
                      const isOpen = mExpanded === key;
                      const hasPath = (e.path?.length ?? 0) > 0;
                      const $2 = (v: any) => (v != null ? `$${Number(v).toFixed(2)}` : '—');
                      return (
                        <React.Fragment key={key}>
                          <TableRow
                            hover
                            onClick={() => hasPath && setMExpanded(isOpen ? null : key)}
                            sx={{ opacity: r ? 1 : 0.6, cursor: hasPath ? 'pointer' : 'default' }}
                          >
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.date}{e.account ? <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>{e.account}</Typography> : null}</TableCell>
                            <TableCell>{fmtTime(s.started_at)}</TableCell>
                            <TableCell>{$2(s.entry_credit_per_share)}</TableCell>
                            <TableCell>{$2(target)}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {$2(paid)} <Chip label="REAL" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
                              {e.concession?.concession_usd != null && (
                                <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                  concession {fmt$(-Number(e.concession.concession_usd))} total ({e.concession.contracts ?? s.contracts ?? '?'} ct)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip label={outcome.replace(/_/g, ' ')} size="small" color={outcomeColor as any} variant={r ? 'filled' : 'outlined'} sx={{ fontSize: '0.6rem', height: 18 }} />
                            </TableCell>
                            <TableCell>{$2(r?.would_debit)}</TableCell>
                            <TableCell>{r?.minutes != null ? `${Math.round(r.minutes)} min` : '—'}</TableCell>
                            <TableCell>{$2(r?.worst_mid)}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: saved == null ? undefined : saved >= 0 ? 'success.main' : 'error.main' }}>{fmt$(saved)}</TableCell>
                            <TableCell sx={{ color: r?.saved_usd_leader == null ? undefined : r.saved_usd_leader >= 0 ? 'success.main' : 'error.main' }}>
                              {fmt$(r?.saved_usd_leader)}{r?.contracts ? ` (${r.contracts} ct)` : ''}
                            </TableCell>
                          </TableRow>
                          {isOpen && hasPath && (
                            <TableRow>
                              <TableCell colSpan={11} sx={{ py: 1, bgcolor: 'rgba(128,128,128,0.06)' }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Spread mid after our close (after-exit tracker, 1/min) — green = target {$2(target)}, red = target + guard {$2(target != null ? Number(target) + Number(s.guard ?? 0.03) : null)}, orange = what we paid {$2(paid)}
                                </Typography>
                                <Box sx={{ height: 160 }}>
                                  <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={e.path} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                                      <XAxis dataKey="t" tick={{ fontSize: 10 }} />
                                      <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10 }} width={44} tickFormatter={(v) => `$${Number(v).toFixed(2)}`} />
                                      <RechartsTooltip formatter={(v: any) => `$${Number(v).toFixed(2)}`} />
                                      {target != null && <ReferenceLine y={Number(target)} stroke="#66bb6a" strokeDasharray="4 3" />}
                                      {target != null && <ReferenceLine y={Number(target) + Number(s.guard ?? 0.03)} stroke="#ef5350" strokeDasharray="4 3" />}
                                      {paid != null && <ReferenceLine y={Number(paid)} stroke="#ffb74d" strokeDasharray="2 2" />}
                                      <Line type="stepAfter" dataKey="mid" stroke="#90caf9" dot={false} strokeWidth={2} isAnimationActive={false} />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {(() => {
              const scored = visible.patient_close.filter((e) => e.result && e.result.saved_per_ct != null);
              if (!scored.length) return null;
              const byDay: { [d: string]: number } = {};
              scored.forEach((e) => { byDay[e.date] = (byDay[e.date] ?? 0) + Number(e.result!.saved_per_ct); });
              const dayVals = Object.values(byDay);
              const sumCt = scored.reduce((a, e) => a + Number(e.result!.saved_per_ct), 0);
              const sumUsd = scored.reduce((a, e) => a + Number(e.result!.saved_usd_leader ?? 0), 0);
              const worse = dayVals.filter((v) => v < 0).length;
              const worst = Math.min(...dayVals);
              const armable = scored.length >= 10 && sumCt > 0 && worst >= -3;
              return (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  n {scored.length} · Σ saved {fmt$(sumCt)}/ct · Σ {fmt$(sumUsd)} leader · {worse} day{worse === 1 ? '' : 's'} worse · worst day {fmt$(worst)}/ct
                  {' '}· rule {armable ? <b style={{ color: '#66bb6a' }}>met</b> : scored.length < 10 ? `accumulating (${scored.length}/10)` : <b style={{ color: '#ef5350' }}>not met</b>}
                </Typography>
              );
            })()}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
              ~10 chased closes; arm (patient live close) only if Σ saved &gt; 0 AND no single day worse than −3/ct — arming is a LIVE exit change, operator&apos;s word.
              Replay baseline (17 chased leader closes 8/25–10/22, 1-min prints): +2c/10m −$56 (9 worse) · <b>+3c/20m (default) +15/ct, +$525, 3 worse</b> · +5c/20m +$1,003 (2 worse).
              A START with no result by 16:00 = unresolved (grey). Never in the P&amp;L.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {/* R12 panel — wall-stability shadow (v0.49.0): per-day flicker counts
          + the trade's three walls; not a study card, data only */}
      {(data?.r12_wall_stability?.length ?? 0) > 0 && (
        <Accordion defaultExpanded={false} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold' }}>Wall-stability shadow — {data!.r12_wall_stability!.length} day(s) (mode-5 gate LIVE since v0.54.0; still scoring run3)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
              The mode-5 entry gate graduated to LIVE on 10/16 (v0.54.0) — blocked candidates now show as wall_unstable stand-aside reasons on the Trade log.
              This panel keeps scoring the still-dark run3 variant and the exit-line question (the recorded exit line stays the raw read).
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Date', 'Put flicker (changes/reads)', 'Decision flips (mode/run)', 'Raw wall', 'Mode wall', 'Run wall', 'Would allow (mode / run)', 'In-hold extreme', 'Lines fired (raw / mode / run)', 'Exit', 'Actual P&L/ct'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data!.r12_wall_stability!.map((e) => {
                    const d = e.eod || {};
                    const t = d.trade;
                    const flips = d.decision_flips || {};
                    const flicker = d.put_reads ? (d.put_changes ?? 0) / d.put_reads : 0;
                    const allow = (v: any) => (v == null ? '—' : v ? 'allow' : 'BLOCK');
                    return (
                      <TableRow key={e.date}>
                        <TableCell>{e.date}</TableCell>
                        <TableCell>
                          {d.put_changes ?? '—'}/{d.put_reads ?? '—'}
                          <Box component="span" sx={{ display: 'inline-block', ml: 0.5, width: 40, height: 6, bgcolor: 'rgba(128,128,128,0.2)', borderRadius: 3, verticalAlign: 'middle' }}>
                            <Box sx={{ width: `${Math.min(100, flicker * 100 * 4)}%`, height: '100%', bgcolor: flicker > 0.15 ? '#ef5350' : '#ffb74d', borderRadius: 3 }} />
                          </Box>
                        </TableCell>
                        <TableCell>{flips.put_mode ?? 0}m / {flips.put_run ?? 0}r{(flips.call_mode || flips.call_run) ? ` · calls ${flips.call_mode ?? 0}m/${flips.call_run ?? 0}r` : ''}</TableCell>
                        <TableCell>{t?.raw_wall ?? '—'}</TableCell>
                        <TableCell>{t?.mode_wall ?? '—'}</TableCell>
                        <TableCell>{t?.run_wall ?? 'unstable'}</TableCell>
                        <TableCell>{t ? `${allow(t.mode_would_allow)} / ${allow(t.run_would_allow)}` : '—'}</TableCell>
                        <TableCell>{t?.spot_extreme_in_hold ?? '—'}</TableCell>
                        <TableCell>
                          {t ? `${fmtTime(t.raw_level_fired_at) || '—'} / ${fmtTime(t.mode_level_fired_at) || '—'} / ${fmtTime(t.run_level_fired_at) || '—'}` : '—'}
                          {t?.raw_level_fired_after_close_at ? ` (raw crossed ${fmtTime(t.raw_level_fired_after_close_at)} after we were flat)` : ''}
                        </TableCell>
                        <TableCell>{t?.exit_reason ? <Chip label={String(t.exit_reason).replace(/_/g, ' ')} size="small" sx={{ fontSize: '0.6rem' }} /> : <span style={{ opacity: 0.6 }}>no trade</span>}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: (t?.actual_pnl_per_ct ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                          {t ? <>{fmt$(t.actual_pnl_per_ct)} <Chip label="REAL" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} /></> : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
              Scoring question: when the stabilized wall would have BLOCKED a trade we took, did we win or get stopped on the raw line?
              When it would have recorded a different exit line, which line did the tape respect? A mode-line fire time equal to the open
              means the stabilized line was already behind spot at entry (that variant would have blocked). Nothing here is a
              recommendation; ~10–15 mornings before any reading.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {/* 3. Roadmap panel — always visible, never filtered */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Roadmap — open items: shadows / dark features being scored, and ideas not yet built
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['#', 'Item', 'Status', 'What moves it', 'Where'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {ROADMAP.map((r) => (
                <TableRow key={r.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{r.id}</TableCell>
                  <TableCell>{r.item}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}><Chip label={r.status} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} /></TableCell>
                  <TableCell>{r.next}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{r.where}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* R10 would-cap ledger — one row per thin-credit leader entry in
            range: P&L at actual size vs at the capped size (data only) */}
        {(data?.r10_thin_credit?.length ?? 0) > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block' }}>
              R10 would-cap ledger ({data!.r10_thin_credit!.length} thin-credit entr{data!.r10_thin_credit!.length === 1 ? 'y' : 'ies'} in range):
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Date', 'Ladder factor', 'Real trade', 'P&L at actual size', 'P&L at capped size', 'Δ'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data!.r10_thin_credit!.map((e) => {
                    const from = e.capped_from ?? e.would_cap_from;
                    const to = e.capped_from != null ? e.applied_factor : e.would_cap_to;
                    const armed = e.capped_from != null;
                    const pnl = e.real?.realized_pnl;
                    // capped size = actual contracts scaled by (cap factor /
                    // applied factor) — P&L/ct is unchanged, only size moves
                    const ratio = !armed && e.would_cap_to != null && (e.applied_factor ?? e.would_cap_from)
                      ? e.would_cap_to / (e.applied_factor ?? e.would_cap_from)! : null;
                    const capped = pnl != null && ratio != null ? pnl * ratio : (armed ? pnl : null);
                    return (
                      <TableRow key={e.date}>
                        <TableCell>{e.date}</TableCell>
                        <TableCell>
                          ×{from ?? '?'} → ×{to ?? '?'} {armed
                            ? <Chip label="capped (armed)" size="small" color="info" sx={{ fontSize: '0.55rem', height: 14 }} />
                            : <Chip label="would-cap (dark)" size="small" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />}
                        </TableCell>
                        <TableCell><RealCell real={e.real} /></TableCell>
                        <TableCell sx={{ fontWeight: 600, color: (pnl ?? 0) >= 0 ? 'success.main' : 'error.main' }}>{fmt$(pnl)}</TableCell>
                        <TableCell>{fmt$(capped)}</TableCell>
                        <TableCell sx={{ color: capped != null && pnl != null && capped - pnl > 0 ? 'success.main' : 'text.secondary' }}>
                          {capped != null && pnl != null ? fmt$(capped - pnl) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              Capped size scales the real P&L by (cap factor / applied factor) — a sizing counterfactual, never a recommendation.
            </Typography>
          </Box>
        )}

        {/* Closed — analyzed and NOT adopted (short collapsed list) */}
        <Accordion sx={{ mt: 1.5, boxShadow: 'none', '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 32, px: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              Closed — analyzed and NOT adopted ({CLOSED.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            {CLOSED.map((c) => (
              <Typography key={c.item} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                <b>{c.item}</b> (closed {c.closed}): {c.verdict}
              </Typography>
            ))}
          </AccordionDetails>
        </Accordion>
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
          Roster and roadmap come verbatim from the strategy's dashboard prompt (edited by the engine's maintainers on every
          release). Graduated studies are deliberately absent — their rows are calibration for live rules and live in the
          Trade log / Exit-reason views; items analyzed and rejected move to the Closed list with their verdict. Nothing on
          this tab may trigger, suggest, or imitate an order.
        </Typography>
      </Paper>
    </Box>
  );
};

export default ShadowsView;
